import { formatTime, formatDateDivider, getDayKey, formatFileSize, linkifyText, escapeHtml, getLastMessagePreview, extractFirstUrl, broadcastUnreadCounts, waitForRoom, isTouchDevice } from "./utils";
import { loadAuthenticatedMedia, resolveAuthenticatedMediaUrl, downloadAuthenticatedFile, showComposerError, mediaCache } from "./media";
import { sendReadReceiptForRoom, showReactionPicker, renderExistingReactions, renderReactionOnMessage } from "./reactions";
import { showAttachmentMenu, showGifPicker } from "./attachments";
import { showAttachmentPreview } from "./attachment_preview";
import { VoiceRecorder } from "./voice_message";
import { voicePlayer, probeAudioDuration } from "./voice_player";
import { attachContextMenu } from "./context_menu";
import { attachImageLightbox } from "./lightbox";
import { createSearchEngine } from "./search_engine";
import { clearHighlights, highlightInElement } from "./search_text";
import { revealEvent, focusMessage } from "./timeline_jump";
import { RoomListSearch } from "./room_list_search";
import { RoomSearch, SEARCH_ICON } from "./room_search";
import { MediaGallery, RoomMediaScanner } from "./media_gallery";

// Sync events arrive in bursts; one count refresh per burst is enough.
const UNREAD_REFRESH_DEBOUNCE_MS = 500;

// A message reaching the client over sync and the appservice transaction that
// makes Rails write its notification are two racing deliveries of the same
// event. Clearing the room too eagerly would clear a notification that has not
// been written yet, so the clear waits — and, landing after the refresh above,
// it also has the last word on the number.
const MARK_READ_DELAY_MS = 1500;

// Loading older messages shows its spinner only past this — see renderTimeline.
const PAGINATION_SPINNER_DELAY_MS = 300;

// The screens stacked on a conversation, as history entries (pushPanelState).
const PANEL_DEPTH = { details: 1, media: 2 };

// Safari on iOS animates its own back gesture and back button with a snapshot
// of the previous entry; the panel must not slide out a second time behind it.
// The native app has no browser back, Android and desktop browsers show none.
function isIosWebBrowser() {
  if (document.body.classList.contains("hotwire-native-app")) return false;
  const ua = navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return isIos;
}

// A jump to a message (jumpToEvent) shows its notice only past the same delay.
// A notice that needs no more answer hides after a while.
const JUMP_NOTICE_DELAY_MS = PAGINATION_SPINNER_DELAY_MS;
const JUMP_NOTICE_MS = 4000;
const JUMP_NOTICE_ACTION_MS = 10000;

// Both indicators live at the top of the composer wrapper so they can be
// positioned relative to the input row. Order matters: the typing indicator
// sits above the new-messages hint.
const INDICATORS_MARKUP = `
  <div class="bryght-typing-indicator" style="display: none;"></div>
  <button type="button" class="bryght-new-messages-indicator" style="display: none;">
    <span class="bryght-new-messages-label"></span>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <polyline points="19 12 12 19 5 12"></polyline>
    </svg>
  </button>
`;

export class BryghtChatUI {
  constructor(client, userId, userMap, t, roomMap = {}) {
    this.client = client;
    this.userId = userId;
    this.userMap = userMap;
    this.t = t;
    this.roomMap = roomMap;

    this.container = document.getElementById("bryght-chat");
    this.leftPanel = this.container?.querySelector("#rooms-container");
    this.rightPanel = this.container?.querySelector("#chat-container");
    this.filterBar = this.container?.querySelector(".bryght-room-filters");

    // Which rooms the list shows: "all", "unread", "direct" or "group".
    this.roomFilter = "all";
    this.setupRoomFilters();

    this.searchEngine = createSearchEngine(client);
    // Rooms whose older events a jump renders in one pass (timeline_jump.js).
    this.backfillSuspensions = new Map();
    this.jumpController = null;
    const roomList = this.container?.querySelector(".bryght-room-list");
    if (roomList?.querySelector(".bryght-room-search__input")) {
      this.roomListSearch = new RoomListSearch(this, roomList);
    }

    // Create the Room Details panel (slides over timeline)
    if (this.rightPanel && !this.rightPanel.querySelector(".bryght-room-details-panel")) {
      this.detailsPanel = document.createElement("div");
      this.detailsPanel.className = "bryght-room-details-panel";
      // Swipe from the left edge goes back to the conversation (swipe_back_controller.js).
      this.detailsPanel.dataset.controller = "swipe-back";
      this.detailsPanel.dataset.swipeBackSwipingClassValue = "is-swiping-details";
      this.rightPanel.appendChild(this.detailsPanel);
    } else {
      this.detailsPanel = this.rightPanel?.querySelector(".bryght-room-details-panel");
    }

    // A back swipe has already carried the panel off; only the state has to
    // follow, without sliding anything (handleSwipeBackCommit).
    this.handleSwipeBackCommit = this.handleSwipeBackCommit.bind(this);
    this.container?.addEventListener("swipe-back:commit", this.handleSwipeBackCommit);

    // Set by the back buttons before history.back(): the popstate that
    // follows slides the panel out. null means the entry was popped some
    // other way (browser back button, Safari's own edge swipe).
    this.pendingBackAnimation = null;
    this.pendingRoomSearch = null;

    this.currentRoomId = null;
    this.currentReplyToEvent = null;
    // The 1:1 conversation on screen that has no room yet (see openDraft).
    this.draft = null;

    // Set up global UI listeners
    if (this.rightPanel) {
      this.rightPanel.addEventListener("swipe-reply:trigger", (e) => {
        const messageEl = e.target.closest(".bryght-message");
        if (!messageEl || !messageEl._matrixEvent) return;
        this.showReplyContext(messageEl._matrixEvent);
        
        const input = document.querySelector(".bryght-composer-input");
        if (input) input.focus();
      });

      this.rightPanel.addEventListener("pointerdown", (e) => {
        if (!e.target.closest(".bryght-composer")) {
          const input = document.querySelector(".bryght-composer-input");
          if (input && document.activeElement === input) {
            input.blur();
          }
        }
      });
    }

    // Capture phase: it runs before Turbo's own popstate listener, so a pop
    // from one of the chat's entries onto the page's Turbo entry can be kept
    // from Turbo (see handleChatPopState).
    this.handleChatPopState = this.handleChatPopState.bind(this);
    window.addEventListener("popstate", this.handleChatPopState, { capture: true });
    // True while the current history entry is one the chat pushed.
    this.ownsHistoryEntry = false;

    this.handleRoomUpdated = (e) => {
      const { roomId, roomInfo } = e.detail || {};
      if (!roomId || !roomInfo) return;

      this.roomMap[roomId] = roomInfo;

      if (this.currentRoomId === roomId) {
        const headerName = document.querySelector(".bryght-chat-header-info .bryght-chat-header-name");
        if (headerName) headerName.textContent = roomInfo.name;

        const headerAvatar = document.querySelector(".bryght-chat-header-info .bryght-room-avatar");
        if (headerAvatar) {
          if (roomInfo.avatar_url) {
            headerAvatar.innerHTML = `<img src="${roomInfo.avatar_url}" alt="" />`;
            headerAvatar.classList.remove("bryght-room-avatar--initials");
          } else {
            headerAvatar.innerHTML = "";
            headerAvatar.textContent = (roomInfo.name || "?").charAt(0).toUpperCase();
            headerAvatar.classList.add("bryght-room-avatar--initials");
          }
        }

        // Re-render the details panel if it is currently open
        if (this.container.classList.contains("is-details-active")) {
          const room = this.client.getRoom(roomId);
          if (room) this.renderRoomDetails(room);
        }
      }

      this.renderRoomList();
    };
    window.addEventListener("bryght-chat:room-updated", this.handleRoomUpdated);

    this.syncNavigationDepth();
  }

  // Retires this instance. The entrypoint replaces it when the container it drew
  // into is no longer the one in the document (see initBryghtChat).
  destroy() {
    this.pendingRoomSearch = null;
    window.removeEventListener("popstate", this.handleChatPopState, { capture: true });
    window.removeEventListener("bryght-chat:room-updated", this.handleRoomUpdated);
    this.container?.removeEventListener("swipe-back:commit", this.handleSwipeBackCommit);
    this.roomListSearch?.destroy();
    this.jumpController?.abort();
  }

  getUserInfo(matrixUserId) {
    if (!matrixUserId) return { name: "?", avatar_url: null, avatar_large_url: null };
    if (this.userMap[matrixUserId]) {
      return this.userMap[matrixUserId];
    }

    this.queueUserResolution(matrixUserId);
    return { name: matrixUserId, avatar_url: null, avatar_large_url: null };
  }

  queueUserResolution(matrixUserId) {
    if (!matrixUserId || this.userMap[matrixUserId]) return;
    if (!this.pendingUserResolutions) this.pendingUserResolutions = new Set();
    if (this.pendingUserResolutions.has(matrixUserId)) return;

    this.pendingUserResolutions.add(matrixUserId);
    this.scheduleBatchResolution();
  }

  queueRoomResolution(roomId) {
    if (!roomId || this.roomMap[roomId]) return;
    if (!this.pendingRoomResolutions) this.pendingRoomResolutions = new Set();
    if (this.pendingRoomResolutions.has(roomId)) return;

    this.pendingRoomResolutions.add(roomId);
    this.scheduleBatchResolution();
  }

  scheduleBatchResolution() {
    if (this._batchResolutionTimer) return;

    this._batchResolutionTimer = setTimeout(async () => {
      this._batchResolutionTimer = null;
      const mxids = Array.from(this.pendingUserResolutions || []);
      const roomIds = Array.from(this.pendingRoomResolutions || []);
      this.pendingUserResolutions = new Set();
      this.pendingRoomResolutions = new Set();

      if (mxids.length === 0 && roomIds.length === 0) return;

      try {
        const res = await fetch("/chat/resolve_identities", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": this.getCsrfToken()
          },
          body: JSON.stringify({ mxids, room_ids: roomIds })
        });

        if (res.ok) {
          const data = await res.json();
          let changed = false;

          if (data.users && Object.keys(data.users).length > 0) {
            Object.assign(this.userMap, data.users);
            changed = true;
          }
          if (data.rooms && Object.keys(data.rooms).length > 0) {
            Object.assign(this.roomMap, data.rooms);
            changed = true;
          }

          if (changed) {
            this.renderRoomList();

            if (this.currentRoomId) {
              const currentRoom = this.client.getRoom(this.currentRoomId);
              if (currentRoom) {
                const info = this.getRoomDisplayInfo(currentRoom);

                // Rails is the authority on the room type, so the layout class
                // set from the placeholder has to be corrected here.
                this.container.classList.toggle("is-direct-room", info.type === "direct");

                const headerName = document.querySelector(".bryght-chat-header-info .bryght-chat-header-name");
                if (headerName) headerName.textContent = info.name;

                const headerAvatar = document.querySelector(".bryght-chat-header-info .bryght-room-avatar");
                if (headerAvatar) {
                  if (info.avatar_url) {
                    headerAvatar.innerHTML = `<img src="${info.avatar_url}" alt="" />`;
                    headerAvatar.classList.remove("bryght-room-avatar--initials");
                  } else {
                    headerAvatar.innerHTML = "";
                    headerAvatar.textContent = (info.name || "?").charAt(0).toUpperCase();
                    headerAvatar.classList.add("bryght-room-avatar--initials");
                  }
                }

                document.querySelectorAll(".bryght-message").forEach((msgEl) => {
                  const ev = msgEl._matrixEvent;
                  if (!ev) return;
                  const senderMxid = ev.getSender();
                  if (data.users && data.users[senderMxid]) {
                    const senderEl = msgEl.querySelector(".bryght-message-sender");
                    if (senderEl) senderEl.textContent = data.users[senderMxid].name;
                  }
                });
              }
            }
          }
        }
      } catch (err) {
        console.error("[BryghtChat] Failed to resolve users/rooms:", err);
      }
    }, 50);
  }

  renderFormattedText(text) {
    const container = document.createElement("div");
    container.className = "bryght-message-text-content";

    const str = typeof text === "string" ? text : (text != null ? String(text) : "");
    const linkified = linkifyText(str);
    const isLong = str && (str.length > 350 || (str.match(/\n/g) || []).length >= 6);

    if (isLong) {
      container.classList.add("is-collapsed");
      container.appendChild(linkified);

      const expandBtn = document.createElement("button");
      expandBtn.className = "bryght-message-expand-btn";
      expandBtn.type = "button";
      expandBtn.textContent = this.t("read_more", "Mehr");
      expandBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const isCollapsed = container.classList.toggle("is-collapsed");
        expandBtn.textContent = isCollapsed ? this.t("read_more", "Mehr") : this.t("read_less", "Weniger");
      });
      expandBtn.addEventListener("pointerdown", (e) => {
        e.stopPropagation();
      });

      const wrapper = document.createElement("div");
      wrapper.className = "bryght-message-text-wrapper";
      wrapper.appendChild(container);
      wrapper.appendChild(expandBtn);
      return wrapper;
    } else {
      container.appendChild(linkified);
      return container;
    }
  }

  // Rails owns room naming: the registry answers first, so a room keeps the name
  // the platform gave it. Rooms the registry does not know (PoC-era rooms the
  // client created itself) fall back to the other DM party, then to "Gruppenraum".
  getRoomDisplayInfo(room) {
    const fromRails = this.roomMap[room.roomId];
    if (fromRails) return fromRails;

    this.queueRoomResolution(room.roomId);

    // Placeholder until Rails answers. It has to carry a type: callers key layout
    // off it — selectRoom toggles is-direct-room, which hides per-message avatars
    // in a 1:1 — and a payload without one reads as a group. The batch handler
    // re-toggles once the real answer lands.
    const otherUser = this.getOtherDMUser(room);
    return otherUser
      ? { ...this.getUserInfo(otherUser.userId), type: "direct" }
      : { name: room.name || this.t("group_room", "Gruppenraum"), avatar_url: null, type: "group" };
  }

  getOtherDMUser(room) {
    // Use getMembers() instead of getJoinedMembers() to also find members
    // who are still in 'invite' state (e.g. in a brand new DM room).
    // Filter out the appservice bot — its MXID never starts with @u_
    // (the user namespace), so it is not a real chat participant.
    const members = room.getMembers()
      .filter(m => m.membership === "join" || m.membership === "invite")
      .filter(m => m.userId.startsWith("@u_"));
    if (members.length > 2) return null;
    return members.find((m) => m.userId !== this.userId) || null;
  }

  hideReplyContext() {
    this.currentReplyToEvent = null;
    const contextEl = document.querySelector(".bryght-composer-reply-context");
    if (contextEl) contextEl.style.display = "none";
  }

  showReplyContext(event) {
    this.currentReplyToEvent = event;
    const contextEl = document.querySelector(".bryght-composer-reply-context");
    if (!contextEl) return;
    
    const senderEl = contextEl.querySelector(".bryght-composer-reply-sender");
    const textEl = contextEl.querySelector(".bryght-composer-reply-text");
    
    const senderInfo = this.getUserInfo(event.getSender());
    senderEl.textContent = senderInfo.name;
    textEl.textContent = getLastMessagePreview({ getLiveTimeline: () => ({ getEvents: () => [event] }) }) || event.getContent().body;
    
    contextEl.style.display = "flex";
  }

  formatRelativeTime(timestamp) {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return this.t("just_now", "Jetzt");
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return this.t("yesterday", "Gestern");
    }
    if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    }
    return date.toLocaleDateString([], { day: "2-digit", month: "2-digit" });
  }

  // The badge's number is Rails' (BRY-928): it is fed by the inbound appservice
  // transactions and therefore correct on pages where this client never runs.
  // Counting the timeline here as well would give the drawer a second, dissenting
  // answer, so the open drawer just re-reads Rails' number.
  scheduleUnreadRefresh() {
    clearTimeout(this._unreadRefreshTimer);
    this._unreadRefreshTimer = setTimeout(() => this.refreshUnreadCounts(), UNREAD_REFRESH_DEBOUNCE_MS);
  }

  async refreshUnreadCounts() {
    try {
      const res = await fetch("/notifications/unread_count", {
        headers: { Accept: "application/json" }
      });
      if (!res.ok) return;

      const data = await res.json();
      broadcastUnreadCounts(data.count, data.chat_count);
    } catch (err) {
      console.error("[BryghtChat] Unread count refresh failed:", err);
    }
  }

  // For a message arriving in the room the user is already looking at: it is
  // read on arrival, but Rails has to have written it down first.
  scheduleMarkRoomRead(room) {
    clearTimeout(this._markReadTimer);
    this._markReadTimer = setTimeout(() => this.markRoomReadInRails(room), MARK_READ_DELAY_MS);
  }

  // Opening a conversation reads it. Rails owns that fact, so it has to hear
  // about it — the Matrix read receipt only travels between clients.
  //
  // Named by the Matrix room id rather than the Rails one: a room the peer
  // created arrives over sync while the drawer is open, so it is in no room map
  // and the Rails id is simply not known here. room.roomId always is.
  async markRoomReadInRails(room) {
    if (!room?.roomId) return;

    try {
      const res = await fetch("/chat_rooms/mark_read", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        },
        body: JSON.stringify({ matrix_room_id: room.roomId })
      });
      if (!res.ok) return;

      const data = await res.json();
      broadcastUnreadCounts(data.count, data.chat_count);
    } catch (err) {
      console.error("[BryghtChat] Marking room read failed:", err);
    }
  }

  // The filter tabs above the room list. A switch only changes which rooms are
  // let through renderRoomList — the rows themselves are built the same way.
  setupRoomFilters() {
    if (!this.filterBar) return;

    this.filterBar.addEventListener("click", (e) => {
      const button = e.target.closest("[data-room-filter]");
      if (button) this.setRoomFilter(button.dataset.roomFilter);
    });
  }

  setRoomFilter(filter) {
    if (this.roomFilter === filter) return;
    this.roomFilter = filter;

    this.filterBar.querySelectorAll("[data-room-filter]").forEach((el) => {
      const isActive = el.dataset.roomFilter === filter;
      el.classList.toggle("button--category--active", isActive);
      el.setAttribute("aria-pressed", String(isActive));
    });

    this.renderRoomList();
  }

  matchesRoomFilter({ displayInfo, isUnread }) {
    switch (this.roomFilter) {
      case "unread": return isUnread;
      case "direct": return displayInfo.type !== "group";
      case "group": return displayInfo.type === "group";
      default: return true;
    }
  }

  // The name typed into the search above the list (RoomListSearch).
  matchesRoomQuery({ displayInfo }) {
    return this.roomListSearch ? this.roomListSearch.matchesRoom(displayInfo) : true;
  }

  renderRoomList() {
    if (!this.leftPanel) return;
    const rooms = this.client.getRooms();
    rooms.sort((a, b) => {
      const tsA = a.getLastActiveTimestamp() || 0;
      const tsB = b.getLastActiveTimestamp() || 0;
      return tsB - tsA;
    });

    const joinedRooms = rooms.filter((r) => r.getMyMembership() === "join");

    // Every row's state is computed before filtering: "Ungelesen", "Direkt" and
    // "Gruppen" all key off values the row needs to render anyway.
    const entries = joinedRooms.map((room) => this.buildRoomListEntry(room));
    this.updateUnreadFilterDot(entries.some((entry) => entry.isUnread));

    const visibleEntries = entries.filter((entry) => this.matchesRoomFilter(entry) && this.matchesRoomQuery(entry));
    this.roomListSearch?.onRoomListRendered(visibleEntries.length);

    if (visibleEntries.length === 0) {
      this.renderEmptyRoomList();
      this.scheduleUnreadRefresh();
      return;
    }

    const noRooms = this.leftPanel.querySelector(".bryght-no-rooms");
    if (noRooms) noRooms.remove();
    this.toggleEmptyScreen(false);

    // Build a map of existing swipe-containers keyed by roomId
    const existingByRoomId = new Map();
    this.leftPanel.querySelectorAll(".swipe-actions[data-room-id]").forEach((el) => {
      existingByRoomId.set(el.dataset.roomId, el);
    });

    // Rooms filtered out are dropped from the DOM exactly like rooms the user
    // left, and rebuilt when the filter lets them back in.
    const activeRoomIds = new Set(visibleEntries.map(({ room }) => room.roomId));

    // Remove elements for rooms the user has left
    existingByRoomId.forEach((el, roomId) => {
      if (!activeRoomIds.has(roomId)) {
        el.remove();
        existingByRoomId.delete(roomId);
      }
    });

    // Update or create elements in sort order
    let previousSibling = null; // tracks the last inserted element for ordering

    visibleEntries.forEach(({ room, displayInfo, isMuted, isUnread, timeLabel }) => {
      let swipeContainer = existingByRoomId.get(room.roomId);

      if (swipeContainer) {
        // ── Update existing element in-place ──
        const roomItem = swipeContainer.querySelector(".bryght-room-item");
        if (roomItem) {
          // Update name
          const nameEl = roomItem.querySelector(".bryght-room-name");
          if (nameEl) {
            const muteIcon = isMuted ? '<svg class="bryght-room-mute-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>' : '';
            nameEl.innerHTML = `${muteIcon}${escapeHtml(displayInfo.name)}`;
          }

          // Update time
          const timeEl = roomItem.querySelector(".bryght-room-time");
          if (timeEl) timeEl.textContent = timeLabel;

          // Update preview
          const previewEl = roomItem.querySelector(".bryght-room-preview");
          if (previewEl) previewEl.innerHTML = getLastMessagePreview(room);

          // Update unread dot
          const bottomEl = roomItem.querySelector(".bryght-room-info-bottom");
          if (bottomEl) {
            const existingDot = bottomEl.querySelector(".bryght-room-unread-dot");
            if (isUnread && !existingDot) {
              bottomEl.insertAdjacentHTML("beforeend", '<div class="bryght-room-unread-dot"></div>');
            } else if (!isUnread && existingDot) {
              existingDot.remove();
            }
          }

          // Update active/unread/muted classes
          roomItem.classList.toggle("active", room.roomId === this.currentRoomId);
          roomItem.classList.toggle("bryght-room-item--unread", isUnread);
          roomItem.classList.toggle("bryght-room-item--muted", isMuted);

          // Update avatar
          const avatarEl = roomItem.querySelector(".bryght-room-avatar");
          if (avatarEl) {
            if (displayInfo.avatar_url) {
              avatarEl.innerHTML = `<img src="${displayInfo.avatar_url}" alt="" />`;
              avatarEl.classList.remove("bryght-room-avatar--initials");
            } else if (displayInfo.type === "group") {
              avatarEl.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>`;
              avatarEl.classList.add("bryght-room-avatar--initials");
            } else {
              const name = displayInfo.name || "?";
              avatarEl.textContent = name.charAt(0).toUpperCase();
              avatarEl.innerHTML = avatarEl.textContent;
              avatarEl.classList.add("bryght-room-avatar--initials");
            }
          }

          // Update mute button state in swipe actions
          const muteBtn = swipeContainer.querySelector(".swipe-actions__button--mute");
          if (muteBtn) muteBtn.classList.toggle("swipe-actions__button--active", isMuted);
        }
      } else {
        // ── Create new element ──
        swipeContainer = this._createRoomElement(room, displayInfo, isMuted, isUnread, timeLabel);
      }

      // ── Ensure correct sort order ──
      const nextSibling = previousSibling ? previousSibling.nextElementSibling : this.leftPanel.firstElementChild;
      if (swipeContainer !== nextSibling) {
        this.leftPanel.insertBefore(swipeContainer, nextSibling);
      }
      previousSibling = swipeContainer;
    });

    this.scheduleUnreadRefresh();
  }

  /** Everything the list row of a room displays, plus what the filters key off. */
  buildRoomListEntry(room) {
    const displayInfo = this.getRoomDisplayInfo(room);
    const isMuted = this.isRoomMuted(room.roomId);

    const events = room.getLiveTimeline().getEvents();
    const lastMessageEvent = events.slice().reverse().find(e => e.getType() === "m.room.message");

    let isUnread = false;
    if (lastMessageEvent && lastMessageEvent.getSender() !== this.userId && room.roomId !== this.currentRoomId) {
      isUnread = !isMuted && !room.hasUserReadEvent(this.userId, lastMessageEvent.getId());
    }

    const lastTs = lastMessageEvent ? lastMessageEvent.getTs() : room.getLastActiveTimestamp();

    return {
      room,
      displayInfo,
      isMuted,
      isUnread,
      timeLabel: lastTs ? this.formatRelativeTime(lastTs) : ""
    };
  }

  // "Nothing here" reads differently with a filter on: the chats exist, this
  // tab just does not show any of them. Only an unfiltered, unsearched list
  // that comes up empty means the user has no chats at all — that one gets the
  // full empty screen, the rest a single line.
  renderEmptyRoomList() {
    this.leftPanel.querySelectorAll(".swipe-actions").forEach((el) => el.remove());

    const searching = Boolean(this.roomListSearch?.isActive());
    const noChatsAtAll = !searching && this.roomFilter === "all";
    this.toggleEmptyScreen(noChatsAtAll);

    // While searching, the message results below answer for an empty list.
    if (noChatsAtAll || searching) {
      this.leftPanel.querySelector(".bryght-no-rooms")?.remove();
      return;
    }

    let emptyEl = this.leftPanel.querySelector(".bryght-no-rooms");
    if (!emptyEl) {
      emptyEl = document.createElement("p");
      emptyEl.className = "bryght-no-rooms";
      this.leftPanel.appendChild(emptyEl);
    }

    emptyEl.textContent = this.t("no_rooms_filtered", "Keine Chats für diesen Filter");
  }

  toggleEmptyScreen(visible) {
    const emptyScreen = this.leftPanel?.querySelector(".bryght-chat-empty");
    if (emptyScreen) emptyScreen.hidden = !visible;
  }

  updateUnreadFilterDot(hasUnread) {
    const dot = this.filterBar?.querySelector(".bryght-room-filter-dot");
    if (dot) dot.hidden = !hasUnread;
  }

  /** Creates a new swipe-actions container for a room (used by renderRoomList for new rooms). */
  _createRoomElement(room, displayInfo, isMuted, isUnread, timeLabel) {
    const swipeContainer = document.createElement("div");
    swipeContainer.className = "swipe-actions";
    swipeContainer.dataset.controller = "swipe-actions";
    swipeContainer.dataset.roomId = room.roomId;
    swipeContainer.dataset.action = "swipe-actions:action->swipe-actions#triggerAction";

    const muteLabel = this.t("mute", "Stumm");
    const actionsEl = document.createElement("div");
    actionsEl.className = "swipe-actions__buttons";
    actionsEl.dataset.swipeActionsTarget = "actions";
    actionsEl.innerHTML = `
      <button class="swipe-actions__button swipe-actions__button--mute${isMuted ? ' swipe-actions__button--active' : ''}" title="${muteLabel}" data-action="click->swipe-actions#triggerAction" data-swipe-action-name="mute">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
      </button>
      <button class="swipe-actions__button swipe-actions__button--delete" title="${this.t("delete", "Löschen")}" data-action="click->swipe-actions#triggerAction" data-swipe-action-name="delete">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
      </button>
    `;
    swipeContainer.appendChild(actionsEl);

    const roomItem = document.createElement("div");
    roomItem.className = "bryght-room-item swipe-actions__content";
    roomItem.dataset.roomId = room.roomId;
    roomItem.dataset.swipeActionsTarget = "content";

    roomItem.appendChild(this.buildRoomAvatar(displayInfo));

    const muteIcon = isMuted ? '<svg class="bryght-room-mute-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>' : '';

    const infoEl = document.createElement("div");
    infoEl.className = "bryght-room-info";
    infoEl.innerHTML = `
      <div class="bryght-room-info-top">
        <span class="bryght-room-name">${muteIcon}${escapeHtml(displayInfo.name)}</span>
        <span class="bryght-room-time">${timeLabel}</span>
      </div>
      <div class="bryght-room-info-bottom">
        <span class="bryght-room-preview">${getLastMessagePreview(room)}</span>
        ${isUnread ? '<div class="bryght-room-unread-dot"></div>' : ''}
      </div>
    `;
    roomItem.appendChild(infoEl);

    if (room.roomId === this.currentRoomId) roomItem.classList.add("active");
    if (isUnread) roomItem.classList.add("bryght-room-item--unread");
    if (isMuted) roomItem.classList.add("bryght-room-item--muted");

    roomItem.addEventListener("click", () => this.selectRoom(room));
    swipeContainer.appendChild(roomItem);

    swipeContainer.addEventListener("swipe-actions:action", (e) => {
      const { name } = e.detail;
      if (name === "delete") {
        this.handleDeleteOrLeave(room);
      } else if (name === "mute") {
        this.toggleMute(room.roomId);
      }
    });

    return swipeContainer;
  }

  // `opts.focusEventId` opens the room at that message (a search hit); in the
  // room already open it only jumps there.
  selectRoom(room, composer = null, opts = {}) {
    if (this.currentRoomId === room.roomId) {
      if (opts.focusEventId) this.jumpToEvent(room, opts.focusEventId, { ts: opts.focusTs, tokens: opts.tokens });
      return;
    }
    this.currentRoomId = room.roomId;
    this.draft = null;
    this.hideReplyContext();
    sessionStorage.setItem("bryght_active_room", room.roomId);
    this.pushConversationState({ room: room.roomId });

    this.leftPanel.querySelectorAll(".bryght-room-item").forEach((el) => {
      const isActive = el.dataset.roomId === room.roomId;
      el.classList.toggle("active", isActive);
      if (isActive) {
        el.classList.remove("bryght-room-item--unread");
        const dot = el.querySelector(".bryght-room-unread-dot");
        if (dot) dot.remove();
      }
    });

    // renderTimeline reports the room as read to Rails and broadcasts the
    // resulting numbers, so no extra refresh is needed here.
    this.renderTimeline(room, composer, opts);
  }

  // On a phone and on the /chat page the conversation is its own screen, so it
  // gets a history entry and the back button closes it (handleChatPopState).
  // A draft that becomes a room replaces its entry rather than stacking a
  // second one — one back press must leave the conversation, not the draft it
  // grew out of.
  pushConversationState(state) {
    if (!this.usesHistory()) return;

    const method = history.state?.draft ? "replaceState" : "pushState";
    history[method]({ chat: "open", ...state }, "");
    this.ownsHistoryEntry = true;
  }

  // The details and the media gallery stack on the conversation the same
  // way, so the browser's back (button or edge swipe) peels them off one at
  // a time. A re-render of an open panel keeps its entry.
  pushPanelState(panel) {
    if (!this.usesHistory()) return;
    if (!this.inConversationState() || history.state.panel === panel) return;

    // Details drawn again over an open gallery take the gallery's entry
    // rather than stacking on it: one back must still leave the details.
    const current = PANEL_DEPTH[history.state.panel] || 0;
    const method = PANEL_DEPTH[panel] > current ? "pushState" : "replaceState";
    history[method]({ ...history.state, panel }, "");
    this.ownsHistoryEntry = true;
  }

  // On a phone and on the /chat page the screens are separate; in the desktop
  // drawer they are not, and the browser history is left alone.
  usesHistory() {
    const isInlineMode = document.body.classList.contains("chat-route-active");
    const breakpointM = getComputedStyle(document.documentElement).getPropertyValue("--breakpoint-m").trim() || "50em";
    const isMobileView = window.matchMedia(`(max-width: ${breakpointM})`).matches;
    return isMobileView || isInlineMode;
  }

  inConversationState() {
    return Boolean(history.state && (history.state.room || history.state.draft));
  }

  // The back buttons pop the entry they pushed, so the browser's own back
  // stays in step; the popstate then slides the panel out.
  goBackInHistory() {
    this.pendingBackAnimation = true;
    history.back();
  }

  // Back to the list: nothing on the right, no draft, nothing remembered for
  // the next load.
  closeConversation({ animate = true } = {}) {
    this.jumpController?.abort();
    this.closeRoomSearch();
    this.withTransition(animate, () => this.container.classList.remove("is-room-active"));
    sessionStorage.removeItem("bryght_active_room");
    this.currentRoomId = null;
    this.draft = null;
    this.leftPanel.querySelectorAll(".bryght-room-item").forEach((el) => {
      el.classList.remove("active");
    });
    this.syncNavigationDepth();
  }

  // Runs a class change with or without its slide. Without: the panel is
  // already where the change puts it (a swipe carried it there, or the
  // browser animated the back itself) and sliding again would show twice.
  withTransition(animate, fn) {
    if (animate) {
      fn();
      return;
    }
    this.container.classList.add("is-transition-suppressed");
    fn();
    void this.container.offsetWidth;
    this.container.classList.remove("is-transition-suppressed");
  }

  // Closes whatever the popped entry no longer covers: the gallery, the
  // details, the conversation — from the top down.
  handleChatPopState(event) {
    const state = event.state || {};

    // Popped from a chat entry back onto the page's own Turbo entry: same
    // document, same URL, nothing to restore. Turbo would still start a
    // restore visit — a silent no-op in the browser, but under Hotwire
    // Native its adapter only reports the start of a same-page visit and
    // never its end, and the app's progress bar waits forever. Turbo never
    // learned of the chat's entries, so it does not need to see them go.
    if (this.ownsHistoryEntry && !state.chat && state.turbo && this.container?.isConnected) {
      event.stopImmediatePropagation();
    }
    this.ownsHistoryEntry = Boolean(state.chat);

    const conversationOpen = Boolean(state.room || state.draft);
    const panel = conversationOpen ? state.panel : null;

    const animate = this.pendingBackAnimation ?? !isIosWebBrowser();
    this.pendingBackAnimation = null;

    if (this.mediaGallery && panel !== "media") {
      this.closeMediaGallery({ animate });
    }
    if (this.container.classList.contains("is-details-active") && panel !== "details" && panel !== "media") {
      this.closeRoomDetails({ animate });
    }
    if (this.container.classList.contains("is-room-active") && !conversationOpen) {
      this.closeConversation({ animate });
      this.scheduleUnreadRefresh();
    }

    // Search from the details starts after their history entry has gone.
    const searchRoom = this.pendingRoomSearch;
    this.pendingRoomSearch = null;
    if (searchRoom && !panel && state.room === searchRoom.roomId &&
        this.currentRoomId === searchRoom.roomId && this.container.isConnected) {
      this.openRoomSearch(searchRoom);
    }
  }

  // A swipe finished: the panel is off-screen already. Switch the state
  // without sliding, then pop the history entry it had; the popstate finds
  // nothing left to close.
  handleSwipeBackCommit(event) {
    const panel = event.target;
    if (panel === this.mediaGallery?.element) {
      this.closeMediaGallery({ animate: false });
      if (history.state?.panel === "media") history.back();
    } else if (panel === this.detailsPanel) {
      this.closeRoomDetails({ animate: false });
      if (history.state?.panel === "details") history.back();
    } else if (panel === this.rightPanel) {
      const hadEntry = this.inConversationState();
      this.closeConversation({ animate: false });
      this.scheduleUnreadRefresh();
      if (hadEntry) history.back();
    }
  }

  // How deep the chat is in its own screens, for the native app's swipe
  // (bridge/chat_navigation_controller.js): 0 list, 1 conversation,
  // 2 details, 3 media. Side by side on a wide screen there is nothing to
  // swipe, so that counts as 0.
  syncNavigationDepth() {
    if (!this.container || !this.rightPanel) return;
    let depth = 0;
    const stacked = getComputedStyle(this.rightPanel).getPropertyValue("--chat-stacked").trim() === "1";
    if (stacked && this.container.classList.contains("is-room-active")) {
      depth = 1;
      if (this.container.classList.contains("is-details-active")) depth = 2;
      if (this.mediaGallery) depth = 3;
    }
    // Keep the latest value available to a bridge connecting after this event.
    this.container.dataset.chatNavigationDepth = String(depth);
    if (this.navigationDepth === depth) return;
    this.navigationDepth = depth;
    this.container.dispatchEvent(new CustomEvent("bryght-chat:navigation-depth", { detail: { depth } }));
  }

  // ── Draft conversation ────────────────────────────────────
  // A 1:1 chat nobody has written in yet. It exists only on this screen: no
  // registry row, no Matrix room, nothing in the peer's list. The first message
  // sent from its composer is what creates the room (provisionDraftRoom) — the
  // way WhatsApp and Signal open a chat, so a conversation that was opened and
  // abandoned leaves nothing behind. Group rooms are created up front and never
  // pass through here.
  //
  // The payload is the one open_chat_draft carries: the peer as a direct-room
  // payload (MatrixChat::DisplayPayload.draft) without a room id.

  openDraft({ roomInfo, matrixUserId }) {
    if (!roomInfo || !roomInfo.peer_user_id) return;

    this.draft = { roomInfo, matrixUserId };
    this.currentRoomId = null;
    this.hideReplyContext();
    sessionStorage.removeItem("bryght_active_room");
    this.pushConversationState({ draft: true });

    // Seat the peer as a sender now, so their first reply renders under a name.
    if (matrixUserId) {
      this.userMap[matrixUserId] = {
        name: roomInfo.name,
        avatar_url: roomInfo.avatar_url,
        avatar_large_url: roomInfo.avatar_large_url
      };
    }

    this.leftPanel.querySelectorAll(".bryght-room-item").forEach((el) => {
      el.classList.remove("active");
    });

    this.renderDraft();
  }

  renderDraft() {
    const { roomInfo } = this.draft;

    this.resetRightPanel();
    this.container.classList.add("is-direct-room");

    // No details panel: there is no room to mute, delete or block from yet.
    this.rightPanel.appendChild(this.renderConversationHeader(roomInfo));

    const timeline = document.createElement("div");
    timeline.className = "bryght-timeline";
    timeline.id = "bryght-timeline";
    timeline.dataset.atBottom = "true";

    const hint = document.createElement("div");
    hint.className = "bryght-draft-hint";
    hint.textContent = this.t("draft_hint", `Noch keine Nachrichten. Schreib ${roomInfo.name} die erste Nachricht.`)
      .replace("%{name}", roomInfo.name);
    timeline.appendChild(hint);
    this.rightPanel.appendChild(timeline);

    this.renderComposer(null);
  }

  // Turns the draft into a room: Rails provisions it (or re-admits us into the
  // one the pair already has — DirectMessageService keeps one room per pair),
  // the SDK announces it over sync, and the screen switches from draft to room.
  // Resolves to the Matrix room id the message goes to.
  //
  // A failure carries a message for the composer in `userMessage`; Rails'
  // own reason (reach refused, homeserver down) wins over the generic one.
  async provisionDraftRoom(draft, composer) {
    if (!draft) throw new Error("no draft open");

    let ok = false;
    let data = {};

    try {
      const res = await fetch("/chat_direct_messages", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        },
        body: JSON.stringify({ target_user_ids: [draft.roomInfo.peer_user_id] })
      });
      ok = res.ok;
      data = await res.json().catch(() => ({}));
    } catch (err) {
      console.error("[BryghtChat] Draft provisioning request failed:", err);
    }

    const errors = Array.isArray(data.errors) ? data.errors : [];
    if (!ok || !data.roomId || !data.roomInfo) {
      throw this.draftError(errors.join(", ") || "draft provisioning failed", errors[0]);
    }

    this.roomMap[data.roomId] = data.roomInfo;
    if (data.matrixUserId) {
      this.userMap[data.matrixUserId] = {
        name: data.roomInfo.name,
        avatar_url: data.roomInfo.avatar_url,
        avatar_large_url: data.roomInfo.avatar_large_url
      };
    }

    const room = await waitForRoom(this.client, data.roomId);
    if (!room) throw this.draftError("room did not arrive over sync");

    // Only switch screens if this draft is still the one on screen — the user
    // may have moved on while the room was being created.
    if (this.draft === draft) {
      this.draft = null;
      this.renderRoomList();
      this.selectRoom(room, composer);
    }

    return room.roomId;
  }

  draftError(reason, userMessage = null) {
    const error = new Error(reason);
    error.userMessage = userMessage
      || this.t("draft_create_failed", "Der Chat konnte nicht erstellt werden. Bitte versuche es erneut.");
    return error;
  }

  // ── Conversation screen ───────────────────────────────────

  // Empties the right panel for a conversation (room or draft) and keeps the
  // details panel alive across it (innerHTML = "" destroyed it).
  resetRightPanel() {
    this.jumpController?.abort();
    this.closeRoomSearch();
    this.closeMediaGallery({ animate: false });
    this.container.classList.remove("is-details-active");
    this.newMessagesCount = 0;
    this.rightPanel.innerHTML = "";
    if (this.detailsPanel) {
      this.detailsPanel.innerHTML = "";
      this.rightPanel.appendChild(this.detailsPanel);
    }
    this.container.classList.add("is-room-active");
    this.syncNavigationDepth();
  }

  // ── Search inside the conversation (room_search.js) ───────

  // Turns the conversation header into the search field. Must run inside the
  // tap that asks for it, or iOS will not focus the field.
  openRoomSearch(room) {
    if (this.roomSearch?.room === room) {
      this.roomSearch.input.focus();
      return;
    }
    this.closeRoomSearch();

    // The details panel has a header of its own; the conversation's is a
    // direct child of the right panel.
    const header = this.rightPanel?.querySelector(":scope > .bryght-chat-header");
    if (!header) return;
    this.roomSearch = new RoomSearch(this, room, header);
    this.roomSearch.open();
  }

  closeRoomSearch() {
    this.roomSearch?.close();
    this.roomSearch = null;
  }

  // ── Jumping to a message ──────────────────────────────────
  // A search hit opens its message wherever it lies in the history;
  // timeline_jump.js loads the history up to it.

  // Opens the message in the conversation on screen — loading history up to
  // it if needed — then centres, marks and flashes it. Resolves with the
  // status revealEvent reported.
  async jumpToEvent(room, eventId, { ts = null, tokens = [], smooth = true } = {}) {
    this.jumpController?.abort();
    const controller = new AbortController();
    this.jumpController = controller;

    // Most jumps land at once; only one that loads history gets a notice.
    const noticeTimer = setTimeout(() => {
      if (!controller.signal.aborted) this.showJumpNotice(this.t("search_jumping", "Lade Nachricht…"));
    }, JUMP_NOTICE_DELAY_MS);
    const result = await revealEvent(this, room, eventId, { ts, signal: controller.signal });
    clearTimeout(noticeTimer);
    if (this.jumpController === controller) this.jumpController = null;
    if (result.status === "aborted" || controller.signal.aborted) return "aborted";

    this.hideJumpNotice();
    if (result.status === "found") {
      if (tokens?.length) {
        clearHighlights(result.element);
        highlightInElement(result.element, tokens);
      }
      focusMessage(document.getElementById("bryght-timeline"), result.element, { smooth: smooth && !result.loadedHistory });
    } else if (result.status === "capped") {
      this.showJumpNotice(this.t("search_jump_capped", "Diese Nachricht liegt weit zurück."), {
        actionLabel: this.t("search_jump_continue", "Weiter laden"),
        onAction: () => this.jumpToEvent(room, eventId, { ts, tokens, smooth: false }),
        hideAfter: JUMP_NOTICE_ACTION_MS
      });
    } else {
      this.showJumpNotice(this.t("search_jump_not_found", "Nachricht nicht verfügbar."), { hideAfter: JUMP_NOTICE_MS });
    }
    return result.status;
  }

  // A small notice below the conversation header, for a jump that takes a
  // while or does not arrive.
  showJumpNotice(text, { actionLabel = null, onAction = null, hideAfter = 0 } = {}) {
    if (!this.rightPanel) return;
    let notice = this.rightPanel.querySelector(".bryght-jump-notice");
    if (!notice) {
      notice = document.createElement("div");
      notice.className = "bryght-jump-notice";
      notice.setAttribute("role", "status");
      this.rightPanel.appendChild(notice);
    }
    clearTimeout(this.jumpNoticeTimer);

    const label = document.createElement("span");
    label.textContent = text;
    notice.replaceChildren(label);

    if (actionLabel && onAction) {
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = actionLabel;
      button.addEventListener("click", () => {
        this.hideJumpNotice();
        onAction();
      });
      notice.appendChild(button);
    }

    if (hideAfter) this.jumpNoticeTimer = setTimeout(() => this.hideJumpNotice(), hideAfter);
  }

  hideJumpNotice() {
    clearTimeout(this.jumpNoticeTimer);
    this.rightPanel?.querySelector(".bryght-jump-notice")?.remove();
  }

  // A jump renders the older events it loads in one pass; meanwhile the
  // entrypoint's per-event rendering skips them. Counted per room, since a
  // jump can start while an aborted one still waits for its page.
  suspendBackfillRendering(roomId) {
    this.backfillSuspensions.set(roomId, (this.backfillSuspensions.get(roomId) || 0) + 1);
  }

  // True once no jump is loading into the room any more.
  resumeBackfillRendering(roomId) {
    const remaining = (this.backfillSuspensions.get(roomId) || 1) - 1;
    if (remaining > 0) {
      this.backfillSuspensions.set(roomId, remaining);
      return false;
    }
    this.backfillSuspensions.delete(roomId);
    return true;
  }

  isBackfillRenderingSuspended(roomId) {
    return this.backfillSuspensions.has(roomId);
  }

  // Renders the loaded events older than everything on screen in one pass —
  // what a jump fetched while the per-event rendering was suspended — so the
  // dividers and grouping are rebuilt once. The view stays where it was, as
  // with the scroll-triggered pagination.
  renderMissingOlderEvents(room, timeline = document.getElementById("bryght-timeline")) {
    if (!timeline) return;

    const rendered = new Set(Array.from(timeline.querySelectorAll(".bryght-message"), (el) => el.dataset.eventId));
    const fragment = document.createDocumentFragment();
    for (const event of room.getLiveTimeline().getEvents()) {
      if (rendered.has(event.getId())) break;
      const msgEl = this.renderTimelineEvent(event);
      if (msgEl) fragment.appendChild(msgEl);
    }
    if (!fragment.childNodes.length) return;

    const anchor = timeline.querySelector(".bryght-message");
    const anchorOffset = anchor ? anchor.offsetTop : 0;
    const spinner = timeline.querySelector(".bryght-pagination-spinner");
    timeline.insertBefore(fragment, spinner ? spinner.nextSibling : timeline.firstChild);
    this.ensureDateDividers(timeline);
    if (anchor) timeline.scrollTop += anchor.offsetTop - anchorOffset;
  }

  // The round avatar of a room: its picture, the group icon or an initial.
  buildRoomAvatar(info, extraClass = "") {
    const avatarEl = document.createElement("div");
    avatarEl.className = `bryght-room-avatar ${extraClass}`.trim();
    if (info.avatar_url) {
      avatarEl.innerHTML = `<img src="${info.avatar_url}" alt="" />`;
    } else if (info.type === "group") {
      avatarEl.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>`;
      avatarEl.classList.add("bryght-room-avatar--initials");
    } else {
      avatarEl.textContent = (info.name || "?").charAt(0).toUpperCase();
      avatarEl.classList.add("bryght-room-avatar--initials");
    }
    return avatarEl;
  }

  // Back button, avatar, name. `onDetails` makes the name open the details
  // panel, `onSearch` adds the search button; a draft has neither.
  renderConversationHeader(info, onDetails = null, onSearch = null) {
    const header = document.createElement("div");
    header.className = "bryght-chat-header";

    const backButton = document.createElement("button");
    backButton.className = "bryght-chat-back-button";
    backButton.title = this.t("back", "Zurück");
    backButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/></svg>`;
    backButton.addEventListener("click", () => {
      if (this.inConversationState()) {
        this.goBackInHistory();
      } else {
        this.closeConversation();
      }
    });
    header.appendChild(backButton);

    const headerInfoWrap = document.createElement("div");
    headerInfoWrap.className = "bryght-chat-header-info";

    const headerAvatar = document.createElement("div");
    headerAvatar.className = "bryght-room-avatar bryght-room-avatar--small";
    if (info.avatar_url) {
      headerAvatar.innerHTML = `<img src="${info.avatar_url}" alt="" />`;
    } else if (info.type === "group") {
      headerAvatar.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>`;
      headerAvatar.classList.add("bryght-room-avatar--initials");
    } else {
      headerAvatar.textContent = (info.name || "?").charAt(0).toUpperCase();
      headerAvatar.classList.add("bryght-room-avatar--initials");
    }
    headerInfoWrap.appendChild(headerAvatar);

    const headerName = document.createElement("h3");
    headerName.className = "bryght-chat-header-name";
    headerName.textContent = info.name;
    headerInfoWrap.appendChild(headerName);

    if (onDetails) {
      headerInfoWrap.classList.add("bryght-chat-header-info--clickable");
      headerInfoWrap.addEventListener("click", (e) => {
        if (e.target.closest('.bryght-chat-back-button')) return;
        onDetails();
      });
    }

    header.appendChild(headerInfoWrap);

    if (onSearch) {
      const actions = document.createElement("div");
      actions.className = "bryght-chat-header__actions";

      const searchButton = document.createElement("button");
      searchButton.type = "button";
      // Sized and hovered like the drawer's fullscreen and close buttons.
      searchButton.className = "button button--only-icon button--transparent bryght-chat-header__action";
      searchButton.title = this.t("search_in_chat", "In diesem Chat suchen");
      searchButton.setAttribute("aria-label", searchButton.title);
      searchButton.innerHTML = SEARCH_ICON;
      searchButton.addEventListener("click", onSearch);
      actions.appendChild(searchButton);
      header.appendChild(actions);
    }

    return header;
  }

  renderTimeline(room, composer = null, opts = {}) {
    const focusedElement = composer?.contains(document.activeElement) ? document.activeElement : null;
    this.resetRightPanel();

    // Toggle direct-room class so CSS can hide per-message avatars in 1:1 chats
    const roomInfo = this.getRoomDisplayInfo(room);
    this.container.classList.toggle("is-direct-room", roomInfo.type === "direct");

    // Header is always clickable — opens Room Details for any room type
    this.rightPanel.appendChild(this.renderConversationHeader(roomInfo, () => this.renderRoomDetails(room), () => this.openRoomSearch(room)));

    const timeline = document.createElement("div");
    timeline.className = "bryght-timeline";
    timeline.id = "bryght-timeline";
    this.rightPanel.appendChild(timeline);

    const triggerEl = document.createElement("div");
    triggerEl.className = "bryght-pagination-trigger";
    triggerEl.style.height = "1px";
    triggerEl.style.flexShrink = "0";
    timeline.appendChild(triggerEl);

    const spinnerEl = document.createElement("div");
    spinnerEl.className = "bryght-pagination-spinner";
    spinnerEl.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="spinner-icon"><line x1="12" y1="2" x2="12" y2="6"></line><line x1="12" y1="18" x2="12" y2="22"></line><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line><line x1="2" y1="12" x2="6" y2="12"></line><line x1="18" y1="12" x2="22" y2="12"></line><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line></svg> <span>${this.t("loading_older", "Lade ältere Nachrichten...")}</span>`;
    spinnerEl.style.display = "none";
    timeline.appendChild(spinnerEl);

    const timelineEvents = room.getLiveTimeline().getEvents();
    let firstUnreadMsgEl = null;
    const eventElements = [];

    timelineEvents.forEach((event) => {
      const msgEl = this.renderTimelineEvent(event);
      if (msgEl) {
        timeline.appendChild(msgEl);
        eventElements.push({ event, msgEl });
      }
    });

    let unreadDivider = null;
    for (const item of eventElements) {
      if (!room.hasUserReadEvent(this.userId, item.event.getId())) {
        firstUnreadMsgEl = item.msgEl;
        break;
      }
    }

    if (firstUnreadMsgEl) {
      unreadDivider = document.createElement("div");
      unreadDivider.className = "bryght-unread-divider";
      unreadDivider.innerHTML = `<span>${this.t("new_messages", "Neue Nachrichten")}</span>`;
      firstUnreadMsgEl.parentNode.insertBefore(unreadDivider, firstUnreadMsgEl);
    }

    this.ensureDateDividers(timeline);

    let isInitializingScroll = true;
    requestAnimationFrame(() => {
      // A search hit opens the room at its message, before the saved
      // position and the unread divider.
      const savedScroll = opts.focusEventId ? null : sessionStorage.getItem(`bryght_scroll_${room.roomId}`);
      if (opts.focusEventId) {
        // Parked at the newest message; the jump centres the hit from there.
        timeline.dataset.atBottom = "false";
        timeline.scrollTop = timeline.scrollHeight;
        this.jumpToEvent(room, opts.focusEventId, { ts: opts.focusTs, tokens: opts.tokens, smooth: false });
      } else if (savedScroll === "BOTTOM") {
        timeline.dataset.atBottom = "true";
        timeline.scrollTop = timeline.scrollHeight;
      } else if (savedScroll) {
        try {
          const data = JSON.parse(savedScroll);
          const targetItem = eventElements.find(i => i.event.getId() === data.eventId);
          if (targetItem) {
            timeline.dataset.atBottom = "false";
            const el = targetItem.msgEl;
            timeline.scrollTop = el.offsetTop + el.offsetHeight + data.offset - timeline.clientHeight;
          } else {
            timeline.dataset.atBottom = "true";
            timeline.scrollTop = timeline.scrollHeight;
          }
        } catch(e) {
          timeline.dataset.atBottom = "false";
          timeline.scrollTop = parseInt(savedScroll, 10) || timeline.scrollHeight;
        }
      } else if (unreadDivider) {
        timeline.dataset.atBottom = "false";
        // The positioned timeline is the divider's offset parent.
        timeline.scrollTop = Math.max(0, unreadDivider.offsetTop - 20);
      } else {
        timeline.dataset.atBottom = "true";
        timeline.scrollTop = timeline.scrollHeight;
      }

      setTimeout(() => {
        isInitializingScroll = false;
        const timelineObj = room.getLiveTimeline();
        const paginationToken = timelineObj.getPaginationToken("b") || (room.oldState && room.oldState.paginationToken);
        
        if (!paginationToken) return;

        const observer = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && timeline.dataset.paginating !== "true") {
            const token = room.getLiveTimeline().getPaginationToken("b") || (room.oldState && room.oldState.paginationToken);
            
            if (token) {
              timeline.dataset.paginating = "true";
              // Only a load that takes a moment gets a spinner; most answer
              // before this fires, and a spinner for a few frames is a flicker.
              const spinnerTimer = setTimeout(() => {
                spinnerEl.style.display = "flex";
              }, PAGINATION_SPINNER_DELAY_MS);
              const hideSpinner = () => {
                clearTimeout(spinnerTimer);
                spinnerEl.style.display = "none";
              };

              const firstMsg = timeline.querySelector('.bryght-message');
              const anchorOffset = firstMsg ? firstMsg.offsetTop : 0;

              this.client.paginateEventTimeline(room.getLiveTimeline(), { backwards: true, limit: 30 }).then(() => {
                hideSpinner();
                this.ensureDateDividers(timeline);
                if (firstMsg) {
                  // Only compensate for the height the older messages added
                  // above the anchor, measured against wherever the timeline
                  // stands *now*. Restoring a scrollTop captured before the
                  // request would throw away everything the user scrolled
                  // while it was in flight — and on iOS assigning scrollTop
                  // mid-gesture kills the momentum as well.
                  const newAnchorOffset = firstMsg.offsetTop;
                  const added = newAnchorOffset - anchorOffset;
                  if (added !== 0) timeline.scrollTop += added;
                }
                timeline.dataset.paginating = "false";
                
                const nextToken = room.getLiveTimeline().getPaginationToken("b");
                if (nextToken) {
                  observer.unobserve(triggerEl);
                  observer.observe(triggerEl);
                } else {
                  observer.disconnect();
                }
              }).catch((err) => {
                hideSpinner();
                timeline.dataset.paginating = "false";
              });
            }
          }
        }, { root: timeline, rootMargin: "250px 0px 0px 0px" });
        
        if (triggerEl) observer.observe(triggerEl);
      }, 500);
    });

    let scrollTimeout;
    timeline.addEventListener("scroll", () => {
      // The flag has to follow the finger, not the debounce, and not the
      // initialization window either: everything that snaps the timeline back
      // to the bottom (media finishing its load, a layout shift, an incoming
      // message) reads it, and a scroll up that is still counted as "at
      // bottom" gets yanked back mid-gesture — which is exactly what a scroll
      // right after opening the room runs into.
      const atBottom = timeline.scrollHeight - timeline.scrollTop - timeline.clientHeight < 50;
      timeline.dataset.atBottom = atBottom ? "true" : "false";
      if (atBottom && this.newMessagesCount) {
        // The new messages have been scrolled into view – the hint has served
        // its purpose.
        this.hideNewMessagesIndicator();
      }

      // Only the saved position waits for initialization to finish: the scroll
      // events the opening position produces must not overwrite it.
      if (isInitializingScroll) return;

      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (timeline.dataset.atBottom === "true") {
          sessionStorage.setItem(`bryght_scroll_${room.roomId}`, "BOTTOM");
          return;
        }

        const scrollBottom = timeline.scrollTop + timeline.clientHeight;
        let lastVisibleEventId = null;
        let offsetFromBottom = 0;
        
        for (const item of eventElements) {
          const el = item.msgEl;
          if (el.offsetTop < scrollBottom) {
            lastVisibleEventId = item.event.getId();
            offsetFromBottom = scrollBottom - (el.offsetTop + el.offsetHeight);
          } else {
            break;
          }
        }
        
        if (lastVisibleEventId) {
          sessionStorage.setItem(`bryght_scroll_${room.roomId}`, JSON.stringify({
            eventId: lastVisibleEventId,
            offset: offsetFromBottom
          }));
        }
      }, 150);
    });

    // Handle layout shifts (e.g. toggling fullscreen vs sidebar)
    const resizeObserver = new ResizeObserver(() => {
      if (isInitializingScroll) return;
      if (timeline.dataset.atBottom === "true") {
        timeline.scrollTop = timeline.scrollHeight;
      }
    });
    resizeObserver.observe(timeline);

    // Keep any follow-up text, selection and recording started while the
    // draft's first message was provisioning the room.
    if (composer) {
      this.rightPanel.appendChild(composer);
      focusedElement?.focus({ preventScroll: true });
    } else {
      this.renderComposer(room);
    }
    sendReadReceiptForRoom(this.client, room);
    this.markRoomReadInRails(room);
  }

  // ── New-messages indicator ────────────────────────────────
  // Counts messages that arrived while the timeline was scrolled up, and
  // offers a jump back to the bottom.

  bindNewMessagesIndicator(wrapper) {
    const button = wrapper.querySelector(".bryght-new-messages-indicator");
    if (!button) return;

    button.addEventListener("click", () => {
      const timeline = document.getElementById("bryght-timeline");
      if (timeline) {
        timeline.scrollTo({ top: timeline.scrollHeight, behavior: "smooth" });
        timeline.dataset.atBottom = "true";
      }
      this.hideNewMessagesIndicator();
    });
  }

  bumpNewMessagesIndicator() {
    this.newMessagesCount = (this.newMessagesCount || 0) + 1;

    const button = document.querySelector(".bryght-new-messages-indicator");
    if (!button) return;

    const count = this.newMessagesCount;
    button.querySelector(".bryght-new-messages-label").textContent = count === 1
      ? this.t("new_messages_indicator_one", "1 neue Nachricht")
      : this.t("new_messages_indicator_other", `${count} neue Nachrichten`).replace("%{count}", count);
    button.style.display = "flex";
  }

  hideNewMessagesIndicator() {
    this.newMessagesCount = 0;

    const button = document.querySelector(".bryght-new-messages-indicator");
    if (button) button.style.display = "none";
  }

  // `room` is null for a draft (renderDraft): the composer then looks the same
  // but has nowhere to send to until its first send creates the room.
  renderComposer(room) {
    const draft = room ? null : this.draft;
    const roomInfo = room ? this.getRoomDisplayInfo(room) : draft.roomInfo;
    if (roomInfo.read_only && !roomInfo.is_admin) {
      const readOnlyMsg = document.createElement("div");
      readOnlyMsg.className = "bryght-composer-wrapper";
      readOnlyMsg.innerHTML = `
        ${INDICATORS_MARKUP}
        <div style="padding: 16px; text-align: center; color: var(--color-text-subdued); font-size: 14px;">${this.t("read_only_msg", "Nur Admins dürfen in dieser Gruppe schreiben")}</div>
      `;
      this.rightPanel.appendChild(readOnlyMsg);
      this.bindNewMessagesIndicator(readOnlyMsg);
      return;
    }

    const composer = document.createElement("div");
    composer.className = "bryght-composer-wrapper";
    composer.innerHTML = `
      ${INDICATORS_MARKUP}
      <div class="bryght-composer-reply-context" style="display: none;">
        <div class="bryght-composer-reply-content">
          <span class="bryght-composer-reply-sender"></span>
          <span class="bryght-composer-reply-text"></span>
        </div>
        <button class="bryght-composer-reply-cancel" aria-label="Abbrechen">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      </div>
      <div class="bryght-composer-recording" style="display: none;">
        <div class="bryght-recording-left">
          <span class="bryght-recording-dot"></span>
          <span class="bryght-recording-timer">0:00</span>
        </div>
        <button class="bryght-recording-cancel" aria-label="${this.t("cancel", "Abbrechen")}">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
          <span>${this.t("cancel", "Abbrechen")}</span>
        </button>
        <button class="bryght-recording-stop" aria-label="${this.t("send", "Senden")}">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="19" x2="12" y2="5"></line>
            <polyline points="5 12 12 5 19 12"></polyline>
          </svg>
        </button>
      </div>
      <div class="bryght-composer">
        <input type="file" class="bryght-composer-file bryght-composer-file--doc" hidden multiple />
        <input type="file" class="bryght-composer-file bryght-composer-file--media" hidden multiple accept="image/*,video/*" />
        <input type="file" class="bryght-composer-file bryght-composer-file--camera" hidden accept="image/*,video/*" capture="environment" />
        <button class="bryght-composer-attach" aria-label="${this.t("attach_file", "Datei anhängen")}" title="${this.t("attach_file", "Datei anhängen")}">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </button>
        <textarea class="bryght-composer-input" placeholder="${this.t("write_message_placeholder", "Nachricht schreiben…")}" rows="1" autocomplete="off" maxlength="5000"></textarea>
        <button class="bryght-composer-gif" aria-label="GIF" title="GIF">
          <svg width="28" height="22" xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" fill="currentColor"><path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-80h560v-560H200v560Zm240-160h60v-240h-60v240Zm-160 0h80q17 0 28.5-11.5T400-400v-80h-60v60h-40v-120h100v-20q0-17-11.5-28.5T360-600h-80q-17 0-28.5 11.5T240-560v160q0 17 11.5 28.5T280-360Zm280 0h60v-80h80v-60h-80v-40h120v-60H560v240ZM200-200v-560 560Z"/></svg>
        </button>
        <button class="bryght-composer-send" aria-label="${this.t("send", "Senden")}" style="display: none;">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="19" x2="12" y2="5"></line>
            <polyline points="5 12 12 5 19 12"></polyline>
          </svg>
        </button>
        <button class="bryght-composer-mic" aria-label="${this.t("record_voice", "Sprachnachricht aufnehmen")}" title="${this.t("record_voice", "Sprachnachricht aufnehmen")}">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="9" y="1" width="6" height="12" rx="3"></rect>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
            <line x1="12" y1="19" x2="12" y2="23"></line>
            <line x1="8" y1="23" x2="16" y2="23"></line>
          </svg>
        </button>
      </div>
    `;
    this.rightPanel.appendChild(composer);
    this.bindNewMessagesIndicator(composer);

    const input = composer.querySelector(".bryght-composer-input");
    const sendBtn = composer.querySelector(".bryght-composer-send");
    const micBtn = composer.querySelector(".bryght-composer-mic");
    const attachBtn = composer.querySelector(".bryght-composer-attach");
    const cancelReplyBtn = composer.querySelector(".bryght-composer-reply-cancel");
    const recordingUI = composer.querySelector(".bryght-composer-recording");
    const recordingTimer = composer.querySelector(".bryght-recording-timer");
    const recordingCancelBtn = composer.querySelector(".bryght-recording-cancel");
    const recordingStopBtn = composer.querySelector(".bryght-recording-stop");
    const composerBar = composer.querySelector(".bryght-composer");

    // Where a message from this composer goes. A room answers with its id; a
    // draft first has to become one (provisionDraftRoom), and every send path —
    // text, file, GIF, voice — asks here right before it sends, so nothing is
    // created until then. The promise is kept: a second send while the first is
    // still creating the room waits for the same room instead of asking Rails
    // twice. A failure is forgotten so the next send can try again.
    let pendingRoomId = null;
    const resolveRoomId = () => {
      if (room) return Promise.resolve(room.roomId);

      if (!pendingRoomId) {
        // An upload may finish after the user has opened another conversation.
        // Its recipient belongs to this composer, never to the current screen.
        pendingRoomId = this.provisionDraftRoom(draft, composer).then((roomId) => {
          room = this.client.getRoom(roomId);
          return roomId;
        }).catch((err) => {
          pendingRoomId = null;
          throw err;
        });
      }
      return pendingRoomId;
    };

    // Typing has no room to be typed into in a draft.
    const sendTypingState = (typing, timeoutMs) => {
      if (!room) return;
      this.client.sendTyping(room.roomId, typing, timeoutMs).catch(() => {});
    };

    // ── Send / Mic button toggle & Auto-resize ────────────
    const autoResizeInput = () => {
      input.style.height = "auto";
      const newHeight = Math.min(input.scrollHeight, 140);
      input.style.height = `${newHeight}px`;
    };

    const updateComposerButtons = () => {
      const hasText = input.value.trim().length > 0;
      sendBtn.style.display = hasText ? "" : "none";
      micBtn.style.display = hasText ? "none" : "";
    };

    if (cancelReplyBtn) {
      cancelReplyBtn.addEventListener("click", () => {
        this.hideReplyContext();
        input.focus();
      });
    }

    const sendMessage = async () => {
      let text = input.value.trim();
      if (!text) return;
      if (text.length > 5000) {
        text = text.slice(0, 5000);
      }
      // Capture and consume the reply before awaiting room creation. Another
      // conversation may have its own reply selected when that request ends.
      const replyToEvent = room && this.currentRoomId === room.roomId ? this.currentReplyToEvent : null;
      if (replyToEvent) this.hideReplyContext();
      input.value = "";
      input.style.height = "auto";
      updateComposerButtons();
      input.focus();

      // Cleared before the room is known so a second Enter while a draft is
      // still becoming a room finds nothing to send twice. If the room never
      // comes, the text goes back where it was.
      let roomId;
      try {
        roomId = await resolveRoomId();
      } catch (err) {
        console.error("[BryghtChat] Send failed, no room:", err);
        showComposerError(composer, err.userMessage || this.t("draft_create_failed", "Der Chat konnte nicht erstellt werden. Bitte versuche es erneut."));
        if (!input.value) {
          input.value = text;
          autoResizeInput();
          updateComposerButtons();
        }
        return;
      }

      try {
        if (replyToEvent) {
          const originalBody = replyToEvent.getContent().body || "";
          const sender = replyToEvent.getSender();

          const content = {
            msgtype: "m.text",
            body: `> <${sender}> ${originalBody.split('\n').join('\n> ')}\n\n${text}`,
            format: "org.matrix.custom.html",
            formatted_body: `<mx-reply><blockquote><a href="https://matrix.to/#/${roomId}/${replyToEvent.getId()}">In reply to</a> <a href="https://matrix.to/#/${sender}">${sender}</a><br>${escapeHtml(originalBody)}</blockquote></mx-reply>${escapeHtml(text)}`,
            "m.relates_to": {
              "m.in_reply_to": { event_id: replyToEvent.getId() }
            }
          };

          await this.client.sendEvent(roomId, "m.room.message", content);
        } else {
          await this.client.sendTextMessage(roomId, text);
        }
      } catch (err) {
        console.error("[BryghtChat] Send failed:", err);
      }
    };

    let typingTimeout = null;
    input.addEventListener("input", () => {
      autoResizeInput();
      updateComposerButtons();
      sendTypingState(true, 4000);
      clearTimeout(typingTimeout);
      typingTimeout = setTimeout(() => sendTypingState(false, 0), 3000);
    });

    const sendWithTypingStop = async () => {
      clearTimeout(typingTimeout);
      sendTypingState(false, 0);
      await sendMessage();
    };

    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendWithTypingStop();
      }
    });
    sendBtn.addEventListener("click", sendWithTypingStop);

    // ── Voice Recording (Tap to record / Tap to stop) ────
    let voiceRecorder = null;

    const showRecordingUI = () => {
      composerBar.style.display = "none";
      recordingUI.style.display = "flex";
      recordingTimer.textContent = "0:00";
    };

    const hideRecordingUI = () => {
      recordingUI.style.display = "none";
      composerBar.style.display = "flex";
    };

    const startRecording = async () => {
      try {
        voiceRecorder = new VoiceRecorder({
          onTick: (duration) => {
            recordingTimer.textContent = VoiceRecorder.formatDuration(duration);
          },
        });
        await voiceRecorder.start();
        showRecordingUI();
      } catch (err) {
        console.error("[BryghtChat] Microphone access denied:", err);
        voiceRecorder = null;
        showComposerError(composer, this.t("microphone_unavailable", "Mikrofon nicht verfügbar. Bitte Zugriff erlauben."));
      }
    };

    const stopAndSendRecording = async () => {
      if (!voiceRecorder || !voiceRecorder.isRecording) return;

      const result = await voiceRecorder.stop();
      hideRecordingUI();
      voiceRecorder = null;

      if (!result || !result.file || result.duration < 0.5) {
        // Too short, discard
        return;
      }

      // Build Matrix voice message content and upload
      const { file, duration, waveform, mimeType } = result;
      const peak = Math.max(0, ...waveform);
      console.log("[BryghtChat] Voice recording:", { bytes: file.size, mimeType, seconds: duration, waveformPeak: peak });

      // The recorder delivered no data at all (seen with the iOS Simulator's
      // microphone): nothing to send but a silent, lengthless message.
      if (file.size === 0) {
        showComposerError(composer, this.t("recording_empty", "Die Aufnahme enthält keine Audiodaten. Bitte Mikrofon prüfen."));
        return;
      }
      const container = document.getElementById("bryght-chat");
      const { homeserverUrl, accessToken } = container.dataset;

      const indicator = document.createElement("div");
      indicator.className = "bryght-upload-indicator";
      indicator.textContent = this.t("sending_voice", "Sprachnachricht wird gesendet…");
      composer.insertAdjacentElement("beforebegin", indicator);

      try {
        const encodedFilename = encodeURIComponent(file.name);
        const uploadUrl = `${homeserverUrl}/_matrix/media/v3/upload?filename=${encodedFilename}`;

        const uploadRes = await fetch(uploadUrl, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": file.type || "application/octet-stream",
          },
          body: file,
        });

        if (!uploadRes.ok) throw new Error(`Upload failed: ${uploadRes.status}`);

        const { content_uri: mxcUrl } = await uploadRes.json();
        // The recording is on this device already: one's own message plays
        // without fetching it back from the homeserver.
        mediaCache.set(mxcUrl, URL.createObjectURL(file));

        const content = {
          body: "Sprachnachricht",
          msgtype: "m.audio",
          url: mxcUrl,
          info: {
            mimetype: mimeType,
            size: file.size,
            duration: Math.round(duration * 1000), // ms
          },
          "org.matrix.msc1767.audio": {
            duration: Math.round(duration * 1000),
            waveform: waveform,
          },
          "org.matrix.msc3245.voice": {},
        };

        await this.client.sendEvent(await resolveRoomId(), "m.room.message", content);
        console.log("[BryghtChat] Voice message sent, duration:", VoiceRecorder.formatDuration(duration));
      } catch (err) {
        console.error("[BryghtChat] Voice message upload failed:", err);
        indicator.textContent = err.userMessage || this.t("voice_send_error", "Sprachnachricht konnte nicht gesendet werden");
        indicator.classList.add("bryght-upload-error");
        setTimeout(() => indicator.remove(), 3000);
        return;
      }

      indicator.remove();
    };

    const cancelRecording = () => {
      if (voiceRecorder) {
        voiceRecorder.cancel();
        voiceRecorder = null;
      }
      hideRecordingUI();
    };

    micBtn.addEventListener("click", startRecording);
    recordingStopBtn.addEventListener("click", stopAndSendRecording);
    recordingCancelBtn.addEventListener("click", cancelRecording);

    attachBtn.addEventListener("click", () => showAttachmentMenu(attachBtn, composer));

    const gifBtn = composer.querySelector(".bryght-composer-gif");
    gifBtn.addEventListener("click", () => showGifPicker(gifBtn, resolveRoomId, this.client, composer));

    // Fokus im Textarea halten: sonst blurrt iOS es beim Antippen eines
    // Composer-Buttons, das Safe-Area-Padding des Wrappers kommt zurück und der
    // Button rutscht unter dem Finger weg, bevor der Klick ausgelöst wird.
    // preventDefault auf mousedown unterdrückt nur den Fokuswechsel – click feuert normal.
    [sendBtn, micBtn, attachBtn, gifBtn].forEach((btn) => {
      btn.addEventListener("mousedown", (e) => e.preventDefault());
    });

    const handleFileSelection = (fileInput) => {
      const files = Array.from(fileInput.files);
      fileInput.value = "";
      if (files.length > 0) {
        showAttachmentPreview(files[0], this.client, resolveRoomId, composer, this.t.bind(this));
      }
    };

    composer.querySelector(".bryght-composer-file--doc").addEventListener("change", (e) => handleFileSelection(e.target));
    composer.querySelector(".bryght-composer-file--media").addEventListener("change", (e) => handleFileSelection(e.target));
    composer.querySelector(".bryght-composer-file--camera").addEventListener("change", (e) => handleFileSelection(e.target));

    // ── Drag & Drop ──────────────────────────────────────
    const chatContainer = document.getElementById("chat-container");
    let dropOverlay = null;
    let dragCounter = 0;

    const showDropOverlay = () => {
      if (dropOverlay) return;
      dropOverlay = document.createElement("div");
      dropOverlay.className = "bryght-drop-overlay";
      const label = document.createElement("span");
      label.className = "bryght-drop-overlay__label";
      label.textContent = this.t("drop_file_here", "Datei hier ablegen");
      dropOverlay.appendChild(label);
      chatContainer.appendChild(dropOverlay);
      requestAnimationFrame(() => dropOverlay.classList.add("bryght-drop-overlay--visible"));
    };

    const hideDropOverlay = () => {
      if (!dropOverlay) return;
      dropOverlay.classList.remove("bryght-drop-overlay--visible");
      const el = dropOverlay;
      dropOverlay = null;
      setTimeout(() => el.remove(), 200);
    };

    chatContainer.addEventListener("dragenter", (e) => {
      e.preventDefault();
      dragCounter++;
      if (e.dataTransfer.types.includes("Files")) showDropOverlay();
    });

    chatContainer.addEventListener("dragover", (e) => {
      e.preventDefault();
    });

    chatContainer.addEventListener("dragleave", (e) => {
      e.preventDefault();
      dragCounter--;
      if (dragCounter <= 0) {
        dragCounter = 0;
        hideDropOverlay();
      }
    });

    chatContainer.addEventListener("drop", (e) => {
      e.preventDefault();
      dragCounter = 0;
      hideDropOverlay();
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        showAttachmentPreview(files[0], this.client, resolveRoomId, composer, this.t.bind(this));
      }
    });

    // ── Paste from clipboard ─────────────────────────────
    input.addEventListener("paste", (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.kind === "file") {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            showAttachmentPreview(file, this.client, resolveRoomId, composer, this.t.bind(this));
          }
          return;
        }
      }
    });

    // Desktop only: on a phone a focused composer pulls the keyboard over
    // half of the conversation the user just opened, so the tap into the
    // field stays theirs. (WKWebView ignores a focus() without a user gesture
    // anyway.) The delay lets the slide-in transition finish first.
    if (!isTouchDevice()) {
      setTimeout(() => {
        input.focus();
      }, 305);
    }
  }

  renderTimelineEvent(event) {
    if (event.getType() !== "m.room.message") return null;

    // Redacted messages get a "deleted" placeholder (WhatsApp/Signal style)
    if (event.isRedacted()) {
      return this.renderRedactedPlaceholder(event);
    }

    const content = event.getContent();
    if (!content.body) return null;

    const sender = event.getSender();
    const isOwn = sender === this.userId;
    const senderInfo = this.getUserInfo(sender);
    const timestamp = event.getTs();

    const wrapper = document.createElement("div");
    wrapper.className = `bryght-message ${isOwn ? "bryght-message--own" : ""}`;
    wrapper.dataset.eventId = event.getId();
    wrapper._matrixEvent = event;
    // A local echo's transaction id: what the server's copy of the same
    // message carries in `unsigned`, so the timeline listener can tell the
    // two apart from two messages (see renderedMessageFor in the entrypoint).
    const txnId = event.getTxnId?.();
    if (txnId) wrapper.dataset.txnId = txnId;

    wrapper.dataset.controller = "swipe-reply";
    wrapper.dataset.swipeReplyTarget = "content";

    if (!isOwn) {
      const avatarEl = document.createElement("div");
      avatarEl.className = "bryght-message-avatar";
      if (senderInfo.avatar_url) {
        avatarEl.innerHTML = `<img src="${senderInfo.avatar_url}" alt="" />`;
      } else {
        avatarEl.textContent = senderInfo.name.charAt(0).toUpperCase();
        avatarEl.classList.add("bryght-message-avatar--initials");
      }
      wrapper.appendChild(avatarEl);
    }

    const bubble = document.createElement("div");
    bubble.className = "bryght-message-bubble";

    if (!isOwn) {
      const nameEl = document.createElement("span");
      nameEl.className = "bryght-message-sender";
      nameEl.textContent = senderInfo.name;
      bubble.appendChild(nameEl);
    }

    const bodyEl = document.createElement("div");
    bodyEl.className = "bryght-message-body";

    const handleMediaLoad = (el) => {
      el.classList.remove("bryght-media-loading");
      const timeline = document.getElementById("bryght-timeline");
      if (timeline && timeline.dataset.atBottom === "true") {
        timeline.scrollTop = timeline.scrollHeight;
      }
    };

    // Detect caption: if filename is set and differs from body, body is a caption
    const hasCaption = content.filename && content.body && content.body !== content.filename;

    if (content.msgtype === "m.image") {
      const imgEl = document.createElement("img");
      imgEl.className = "bryght-message-image bryght-media-loading";
      imgEl.alt = content.filename || content.body || this.t("image", "Bild");
      imgEl.loading = "lazy";
      imgEl.onload = () => handleMediaLoad(imgEl);
      imgEl.onerror = () => handleMediaLoad(imgEl);
      loadAuthenticatedMedia(content.url, imgEl, "image", content.info?.mimetype);
      attachImageLightbox(imgEl, content, this.t("image", "Bild"));
      bodyEl.appendChild(imgEl);
    } else if (content.msgtype === "m.video") {
      const videoEl = document.createElement("video");
      videoEl.className = "bryght-message-video bryght-media-loading";
      videoEl.controls = true;
      videoEl.preload = "metadata";
      videoEl.onloadedmetadata = () => handleMediaLoad(videoEl);
      videoEl.onerror = () => handleMediaLoad(videoEl);
      loadAuthenticatedMedia(content.url, videoEl, "video", content.info?.mimetype);
      bodyEl.appendChild(videoEl);
    } else if (content.msgtype === "m.audio") {
      const isVoiceMessage = content["org.matrix.msc3245.voice"] !== undefined;

      if (isVoiceMessage) {
        // ── Custom voice message player ────────────────────
        const voiceEl = document.createElement("div");
        voiceEl.className = "bryght-voice-message";

        const playBtn = document.createElement("button");
        playBtn.className = "bryght-voice-message__play";
        playBtn.innerHTML = `<svg class="icon-play" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="6,3 20,12 6,21"/></svg><svg class="icon-pause" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style="display:none"><rect x="5" y="3" width="4" height="18"/><rect x="15" y="3" width="4" height="18"/></svg>`;
        voiceEl.appendChild(playBtn);

        // Waveform bars
        const waveformContainer = document.createElement("div");
        waveformContainer.className = "bryght-voice-message__waveform";

        const audioData = content["org.matrix.msc1767.audio"];
        let waveformValues = audioData?.waveform;
        if (!waveformValues || waveformValues.length === 0) {
          // Generate placeholder waveform
          waveformValues = Array.from({ length: 32 }, () => Math.floor(Math.random() * 600 + 100));
        }

        // Normalize and render bars. Each bar is the loudest value of its
        // stretch of the waveform — picking one value per bar by index skipped
        // every fourth value, and with it any peak that happened to sit there.
        const maxVal = Math.max(...waveformValues, 1);
        const barCount = Math.min(waveformValues.length, 48);
        const step = waveformValues.length / barCount;
        for (let i = 0; i < barCount; i++) {
          const start = Math.floor(i * step);
          const end = Math.max(start + 1, Math.floor((i + 1) * step));
          const peak = Math.max(...waveformValues.slice(start, end));
          const normalized = peak / maxVal;
          const bar = document.createElement("div");
          bar.className = "bryght-voice-message__bar";
          bar.style.height = `${Math.max(8, normalized * 100)}%`;
          waveformContainer.appendChild(bar);
        }
        voiceEl.appendChild(waveformContainer);

        // Duration label. The event carries the length the recorder measured;
        // the decoded file is only asked as a fallback when the event has none.
        // Never the other way round: a MediaRecorder file (Chrome's webm, some
        // Safari mp4s) has no length header and reports Infinity, which would
        // read as "Infinity:NaN" and leave the progress bars stuck at zero —
        // exactly the messages this browser recorded itself, i.e. one's own.
        let knownDuration = (content.info?.duration || audioData?.duration || 0) / 1000;
        const durationLabel = document.createElement("span");
        durationLabel.className = "bryght-voice-message__duration";
        durationLabel.textContent = VoiceRecorder.formatDuration(knownDuration);
        voiceEl.appendChild(durationLabel);

        // Playback runs in the page-wide voice player (voice_player.js); this
        // message only shows its state and passes the taps on. Everything the
        // player needs is read at tap time, so a name resolved later is used.
        const mimeType = content.info?.mimetype;
        const track = () => ({
          key: content.url,
          mxcUrl: content.url,
          mimeType,
          duration: knownDuration,
          title: this.getUserInfo(sender).name,
          artist: this.t("voice_message", "Sprachnachricht"),
          album: this.roomMap[event.getRoomId()]?.name || "",
          artwork: this.getUserInfo(sender).avatar_large_url || this.getUserInfo(sender).avatar_url || null,
        });

        // Fetched now, so a tap can start playback right away — iOS only lets
        // a tap start audio while it is still being handled. A message from a
        // client that sent no length gets it from the file.
        resolveAuthenticatedMediaUrl(content.url, "audio", mimeType).then((url) => {
          if (url && !knownDuration) probeAudioDuration(url, (seconds) => {
            knownDuration = seconds;
            if (!voicePlayer.isCurrent(content.url)) durationLabel.textContent = VoiceRecorder.formatDuration(seconds);
          });
        });

        const iconPlay = playBtn.querySelector(".icon-play");
        const iconPause = playBtn.querySelector(".icon-pause");
        const bars = waveformContainer.querySelectorAll(".bryght-voice-message__bar");

        voiceEl._voiceKey = content.url;
        voiceEl._renderVoiceState = ({ active, playing, position, duration }) => {
          iconPlay.style.display = playing ? "none" : "";
          iconPause.style.display = playing ? "" : "none";
          voiceEl.classList.toggle("bryght-voice-message--playing", playing);

          const total = duration || knownDuration;
          const progress = active && total ? Math.min(1, position / total) : 0;
          const playedCount = Math.floor(progress * bars.length);
          bars.forEach((b, idx) => b.classList.toggle("bryght-voice-message__bar--played", idx < playedCount));
          // Remaining time while a message is under way, its length otherwise.
          durationLabel.textContent = VoiceRecorder.formatDuration(progress > 0 ? Math.max(0, total - position) : total);
        };
        // A message drawn again — the room reopened, the chat came back after a
        // visit elsewhere — shows a playback that is already running.
        if (voicePlayer.isCurrent(content.url)) voiceEl._renderVoiceState(voicePlayer.stateFor(content.url));

        playBtn.addEventListener("click", () => voicePlayer.toggle(track()));

        // Click on waveform to seek
        waveformContainer.addEventListener("click", (e) => {
          const rect = waveformContainer.getBoundingClientRect();
          const ratio = Math.min(0.98, Math.max(0, (e.clientX - rect.left) / rect.width));
          voicePlayer.seekTrack(track(), ratio);
        });

        bodyEl.appendChild(voiceEl);
      } else {
        // Regular audio file – use standard controls
        const audioEl = document.createElement("audio");
        audioEl.controls = true;
        audioEl.className = "bryght-message-audio";
        loadAuthenticatedMedia(content.url, audioEl, "audio", content.info?.mimetype);
        bodyEl.appendChild(audioEl);
      }
    } else if (content.msgtype === "m.file") {
      const displayName = content.filename || content.body || this.t("file", "Datei");
      const downloadName = content.filename || content.body || "download";
      const fileLink = document.createElement("a");
      fileLink.className = "bryght-message-file";
      fileLink.textContent = `📎 ${displayName}`;
      fileLink.href = "#";
      fileLink.addEventListener("click", (e) => {
        e.preventDefault();
        downloadAuthenticatedFile(content.url, downloadName);
      });
      if (content.info?.size) {
        const sizeEl = document.createElement("span");
        sizeEl.className = "bryght-message-file-size";
        sizeEl.textContent = formatFileSize(content.info.size);
        const wrapperEl = document.createElement("div");
        wrapperEl.appendChild(fileLink);
        wrapperEl.appendChild(sizeEl);
        bodyEl.appendChild(wrapperEl);
      } else {
        bodyEl.appendChild(fileLink);
      }
    }

    // Show caption below media if present (same bubble, like WhatsApp)
    if (hasCaption && ["m.image", "m.video", "m.audio", "m.file"].includes(content.msgtype)) {
      const captionEl = document.createElement("div");
      captionEl.className = "bryght-message-caption";
      captionEl.appendChild(this.renderFormattedText(content.body));
      bodyEl.appendChild(captionEl);
    } else if (content.format === "org.matrix.custom.html" && content.formatted_body && content.formatted_body.includes("<mx-reply>")) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content.formatted_body, "text/html");
      const replyBlock = doc.querySelector("mx-reply");
      if (replyBlock) {
        const quoteEl = document.createElement("div");
        quoteEl.className = "bryght-message-quote";
        
        const senderLink = replyBlock.querySelector("a[href^='https://matrix.to/#/@']");
        let senderName = senderLink ? senderLink.textContent : "Jemand";
        if (senderLink) {
           const mxidMatch = senderLink.getAttribute('href').match(/https:\/\/matrix\.to\/#\/(@.+)/);
           if (mxidMatch) {
              const senderInfo = this.getUserInfo(mxidMatch[1]);
              senderName = senderInfo.name || senderName;
           }
        }
        
        const quoteSenderEl = document.createElement("span");
        quoteSenderEl.className = "bryght-message-quote-sender";
        quoteSenderEl.textContent = senderName;
        quoteEl.appendChild(quoteSenderEl);
        
        let quoteIconHtml = "";
        let hasMediaIcon = false;
        const relatesTo = content["m.relates_to"];
        const replyEventId = relatesTo && relatesTo["m.in_reply_to"] ? relatesTo["m.in_reply_to"].event_id : null;
        
        if (replyEventId) {
           const room = this.client.getRoom(event.getRoomId());
           if (room) {
             const origEvent = room.getLiveTimeline().getEvents().find(e => e.getId() === replyEventId);
             if (origEvent) {
                const origMsgtype = origEvent.getContent().msgtype;
                if (origMsgtype === "m.image" || origMsgtype === "m.video") {
                   quoteIconHtml = `<span class="icon">${document.getElementById("tpl-icon-media")?.innerHTML || "📷"}</span> `;
                   hasMediaIcon = true;
                } else if (origMsgtype === "m.file") {
                   quoteIconHtml = `<span class="icon">${document.getElementById("tpl-icon-doc")?.innerHTML || "📎"}</span> `;
                   hasMediaIcon = true;
                }
             }
           }
        }
        
        let quoteText = "";
        const bq = replyBlock.querySelector("blockquote");
        if (bq && !hasMediaIcon) {
          const clone = bq.cloneNode(true);
          // Remove all links (sender links etc.)
          const links = clone.querySelectorAll("a");
          links.forEach(l => l.remove());
          // Get text and strip nested reply fallback lines ("> ..." prefixed lines from parent replies)
          const rawText = clone.textContent.trim();
          const lines = rawText.split("\n");
          const cleanLines = [];
          let inFallback = true;
          for (const line of lines) {
            if (inFallback && (line.startsWith("> ") || line.trim() === "")) {
              continue; // skip nested fallback lines
            }
            inFallback = false;
            cleanLines.push(line);
          }
          quoteText = cleanLines.join("\n").trim();
        }
        
        const textNode = document.createElement("span");
        textNode.className = "bryght-message-quote-content";
        textNode.innerHTML = quoteIconHtml + escapeHtml(quoteText);
        quoteEl.appendChild(textNode);
        
        quoteEl.addEventListener("click", (e) => {
          e.preventDefault();
          // Reaches the quoted message even when it is older than what is loaded.
          const quotedRoom = this.client.getRoom(event.getRoomId());
          if (replyEventId && quotedRoom) this.jumpToEvent(quotedRoom, replyEventId);
        });
        
        bodyEl.appendChild(quoteEl);
        
        replyBlock.remove();
        const restText = doc.body.textContent.trim();
        bodyEl.appendChild(this.renderFormattedText(restText));
      } else {
        bodyEl.appendChild(this.renderFormattedText(content.body));
      }
    } else if (content.msgtype === "m.text" || content.msgtype === "m.notice") {
      bodyEl.appendChild(this.renderFormattedText(content.body));
    }
    bubble.appendChild(bodyEl);
    
    // URL Previews
    if (content.msgtype === "m.text" || content.msgtype === "m.notice") {
      let rawTextForLink = content.body;
      if (content.format === "org.matrix.custom.html" && content.formatted_body) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content.formatted_body, "text/html");
        const replyBlock = doc.querySelector("mx-reply");
        if (replyBlock) {
          replyBlock.remove();
          rawTextForLink = doc.body.textContent.trim();
        }
      }
      
      const url = extractFirstUrl(rawTextForLink);
      
      if (url) {
        const previewContainer = document.createElement("div");
        bodyEl.appendChild(previewContainer);
        
        const buildPreviewDOM = (preview, previewUrl) => {
          const a = document.createElement("a");
          a.className = "bryght-link-preview";
          a.href = previewUrl;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          
          const imageUrl = preview["og:image"];
          let img = null;
          if (imageUrl && imageUrl.startsWith("mxc://")) {
            img = document.createElement("img");
            img.className = "bryght-link-preview-image";
            img.loading = "lazy";
            img.alt = "";
            img.onload = () => {
              const timeline = document.getElementById("bryght-timeline");
              if (timeline && timeline.dataset.atBottom === "true") {
                timeline.scrollTop = timeline.scrollHeight;
              }
            };
            loadAuthenticatedMedia(imageUrl, img, "image");
            a.appendChild(img);
          } else if (imageUrl) {
            img = document.createElement("img");
            img.className = "bryght-link-preview-image";
            img.loading = "lazy";
            img.alt = "";
            img.onload = () => {
              const timeline = document.getElementById("bryght-timeline");
              if (timeline && timeline.dataset.atBottom === "true") {
                timeline.scrollTop = timeline.scrollHeight;
              }
            };
            img.src = imageUrl;
            a.appendChild(img);
          }
          
          const contentDiv = document.createElement("div");
          contentDiv.className = "bryght-link-preview-content";
          
          if (preview["og:title"]) {
            const titleEl = document.createElement("div");
            titleEl.className = "bryght-link-preview-title";
            titleEl.textContent = preview["og:title"];
            contentDiv.appendChild(titleEl);
          }
          
          if (preview["og:description"]) {
            const descEl = document.createElement("div");
            descEl.className = "bryght-link-preview-description";
            descEl.textContent = preview["og:description"];
            contentDiv.appendChild(descEl);
          }
          
          if (preview["og:site_name"]) {
            const siteEl = document.createElement("div");
            siteEl.className = "bryght-link-preview-domain";
            siteEl.textContent = preview["og:site_name"];
            contentDiv.appendChild(siteEl);
          }
          
          a.appendChild(contentDiv);
          return a;
        };
        
        const cacheKey = `preview_${url}`;
        const cached = sessionStorage.getItem(cacheKey);
        let hasValidCachedPreview = false;
        
        if (cached) {
          try {
            const preview = JSON.parse(cached);
            if (preview && preview.error) {
              sessionStorage.removeItem(cacheKey);
            } else if (preview && (preview["og:title"] || preview["og:description"])) {
              const a = buildPreviewDOM(preview, url);
              previewContainer.appendChild(a);
              hasValidCachedPreview = true;
            }
          } catch (e) {
            sessionStorage.removeItem(cacheKey);
          }
        }
        
        if (!hasValidCachedPreview) {
          const fetchPreview = async (previewUrl) => {
            const apiPath = `/chat/link_preview?url=${encodeURIComponent(previewUrl)}`;
            const res = await fetch(apiPath);
            if (!res.ok) throw new Error(`Preview failed: ${res.status}`);
            return res.json();
          };
          
          fetchPreview(url).then(preview => {
            if (!preview || (!preview["og:title"] && !preview["og:description"])) {
              return;
            }
            sessionStorage.setItem(cacheKey, JSON.stringify(preview));
            
            const a = buildPreviewDOM(preview, url);
            previewContainer.appendChild(a);
            
            const timeline = document.getElementById("bryght-timeline");
            if (timeline && timeline.dataset.atBottom === "true") {
              timeline.scrollTop = timeline.scrollHeight;
            }
          }).catch(err => {
            console.error("Preview error:", err);
          });
        }
      }
    }

    const reactBtn = document.createElement("button");
    reactBtn.className = "bryght-react-btn";
    reactBtn.textContent = "😀";
    reactBtn.title = this.t("react", "Reagieren");
    reactBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      showReactionPicker(event.getRoomId(), event.getId(), this.client, reactBtn);
    });
    bubble.appendChild(reactBtn);

    const reactionsEl = document.createElement("div");
    reactionsEl.className = "bryght-reactions";
    reactionsEl.dataset.eventId = event.getId();
    bubble.appendChild(reactionsEl);

    renderExistingReactions(event, reactionsEl, this.client);

    const contentEl = document.createElement("div");
    contentEl.className = "bryght-message-content";
    contentEl.appendChild(bubble);

    const timeEl = document.createElement("time");
    timeEl.className = "bryght-message-time";
    timeEl.textContent = formatTime(timestamp);
    contentEl.appendChild(timeEl);

    wrapper.appendChild(contentEl);

    // Attach iOS-style long-press context menu
    attachContextMenu(wrapper, { event, client: this.client, chatUI: this, isOwn });

    // An open in-chat search marks and counts every message rendered after it.
    this.roomSearch?.decorate(wrapper);

    return wrapper;
  }

  // ── Redacted message placeholder ─────────────────────────

  renderRedactedPlaceholder(event) {
    const sender = event.getSender();
    const isOwn = sender === this.userId;
    const timestamp = event.getTs();

    const wrapper = document.createElement("div");
    wrapper.className = `bryght-message ${isOwn ? "bryght-message--own" : ""}`;
    wrapper.dataset.eventId = event.getId();
    wrapper._matrixEvent = event;

    if (!isOwn) {
      const senderInfo = this.getUserInfo(sender);
      const avatarEl = document.createElement("div");
      avatarEl.className = "bryght-message-avatar";
      if (senderInfo.avatar_url) {
        avatarEl.innerHTML = `<img src="${senderInfo.avatar_url}" alt="" />`;
      } else {
        avatarEl.textContent = senderInfo.name.charAt(0).toUpperCase();
        avatarEl.classList.add("bryght-message-avatar--initials");
      }
      wrapper.appendChild(avatarEl);
    }

    const bubble = document.createElement("div");
    bubble.className = "bryght-message-bubble bryght-message-bubble--redacted";

    const bodyEl = document.createElement("div");
    bodyEl.className = "bryght-message-body bryght-message-redacted";
    bodyEl.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg> <em>${this.t("message_deleted", "Diese Nachricht wurde gelöscht.")}</em>`;
    bubble.appendChild(bodyEl);

    const contentEl = document.createElement("div");
    contentEl.className = "bryght-message-content";
    contentEl.appendChild(bubble);

    const timeEl = document.createElement("time");
    timeEl.className = "bryght-message-time";
    timeEl.textContent = formatTime(timestamp);
    contentEl.appendChild(timeEl);

    wrapper.appendChild(contentEl);
    return wrapper;
  }

  // ── Mute helpers ──────────────────────────────────────────

  isRoomMuted(roomId) {
    try {
      const pushRules = this.client.pushRules?.global?.room || [];
      return pushRules.some(r => r.rule_id === roomId && r.enabled !== false &&
        (r.actions?.length === 0 || (r.actions?.length === 1 && r.actions[0] === "dont_notify")));
    } catch (_e) {
      return false;
    }
  }

  async toggleMute(roomId) {
    const isMuted = this.isRoomMuted(roomId);
    try {
      if (isMuted) {
        await this.client.deletePushRule("global", "room", roomId);
        // Manually remove from local cache (SDK only updates on next /sync)
        const rules = this.client.pushRules?.global?.room;
        if (rules) {
          const idx = rules.findIndex(r => r.rule_id === roomId);
          if (idx !== -1) rules.splice(idx, 1);
        }
      } else {
        const newRule = {
          rule_id: roomId,
          actions: ["dont_notify"],
          conditions: [{ kind: "event_match", key: "room_id", pattern: roomId }],
          enabled: true
        };
        await this.client.addPushRule("global", "room", roomId, {
          actions: ["dont_notify"],
          conditions: [{ kind: "event_match", key: "room_id", pattern: roomId }]
        });
        // Manually add to local cache (SDK only updates on next /sync)
        if (!this.client.pushRules) this.client.pushRules = { global: { room: [] } };
        if (!this.client.pushRules.global) this.client.pushRules.global = { room: [] };
        if (!this.client.pushRules.global.room) this.client.pushRules.global.room = [];
        this.client.pushRules.global.room.push(newRule);
      }
    } catch (err) {
      console.error("[BryghtChat] Failed to toggle mute:", err);
    }
    this.renderRoomList();
  }

  // ── Delete / Leave handler ────────────────────────────────

  async handleDeleteOrLeave(room) {
    const displayInfo = this.getRoomDisplayInfo(room);

    const confirmMsg = displayInfo.type === "group"
      ? this.t("confirm_leave_group", "Möchtest du diese Gruppe wirklich verlassen?")
      : this.t("confirm_delete_chat", "Möchtest du diesen Chat wirklich löschen?");

    if (!confirm(confirmMsg)) return;

    const chatRoomId = displayInfo.id;
    if (!chatRoomId) {
      // Fallback: direct Matrix leave for rooms without a registry id
      try {
        await this.client.leave(room.roomId);
        this.renderRoomList();
      } catch (err) {
        console.error("[BryghtChat] Failed to leave room:", err);
      }
      return;
    }

    try {
      const res = await fetch(`/chat_rooms/${chatRoomId}/leave`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        }
      });

      if (res.ok) {
        // Clear current room if it was the one deleted
        if (this.currentRoomId === room.roomId) {
          this.currentRoomId = null;
          sessionStorage.removeItem("bryght_active_room");
          this.container.classList.remove("is-room-active");
          if (this.rightPanel) {
            this.rightPanel.innerHTML = `<div class="bryght-empty-state"><p>${this.t("select_chat_title", "Wähle einen Chat")}</p></div>`;
          }
          this.syncNavigationDepth();
        }
        this.renderRoomList();
      } else {
        const data = await res.json().catch(() => ({}));
        console.error("[BryghtChat] Leave failed:", data.errors);
      }
    } catch (err) {
      console.error("[BryghtChat] Leave request failed:", err);
    }
  }

  // ── Room Details view ─────────────────────────────────────

  renderRoomDetails(room) {
    if (!this.detailsPanel) return;

    const info = this.getRoomDisplayInfo(room);
    const isGroup = info.type === "group";
    const isDirect = info.type === "direct";
    const isAdmin = info.is_admin === true;
    const isCreator = info.is_creator === true;
    const isMuted = this.isRoomMuted(room.roomId);

    this.closeMediaGallery({ animate: false });
    this.detailsPanel.innerHTML = "";

    // ── Back button ──
    const header = document.createElement("div");
    header.className = "bryght-chat-header";

    const backBtn = document.createElement("button");
    backBtn.className = "bryght-chat-back-button bryght-chat-back-button--visible";
    backBtn.title = this.t("back", "Zurück");
    backBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/></svg>`;
    backBtn.addEventListener("click", () => {
      if (history.state?.panel === "details") {
        this.goBackInHistory();
      } else {
        this.closeRoomDetails();
      }
    });
    header.appendChild(backBtn);

    const headerTitle = document.createElement("h3");
    headerTitle.className = "bryght-chat-header-name";
    headerTitle.textContent = this.t("room_details_title", "Details");
    header.appendChild(headerTitle);

    this.detailsPanel.appendChild(header);

    // ── Content ──
    const content = document.createElement("div");
    content.className = "bryght-room-details";

    // Avatar (large). The list-sized thumb only as a fallback for a payload
    // that predates avatar_large_url — it is far too small for 5rem.
    const avatarWrap = document.createElement("div");
    avatarWrap.className = "bryght-room-details__avatar";
    const avatar = document.createElement("div");
    avatar.className = "bryght-room-avatar bryght-room-avatar--large";
    const largeAvatarUrl = info.avatar_large_url || info.avatar_url;
    if (largeAvatarUrl) {
      avatar.innerHTML = `<img src="${largeAvatarUrl}" alt="" />`;
    } else if (isGroup) {
      avatar.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>`;
      avatar.classList.add("bryght-room-avatar--initials");
    } else {
      avatar.textContent = (info.name || "?").charAt(0).toUpperCase();
      avatar.classList.add("bryght-room-avatar--initials");
    }
    avatarWrap.appendChild(avatar);
    content.appendChild(avatarWrap);

    // Name — the same display name the room list shows (username if the user
    // set one, otherwise their full name). No @handle line under it.
    const nameEl = document.createElement("h2");
    nameEl.className = "bryght-room-details__name";
    nameEl.textContent = info.name;
    content.appendChild(nameEl);

    // Subtitle (description, groups only)
    if (isGroup && info.description) {
      const descEl = document.createElement("p");
      descEl.className = "bryght-room-details__subtitle";
      descEl.textContent = info.description;
      content.appendChild(descEl);
    }

    // ── Action buttons ──
    const actions = document.createElement("div");
    actions.className = "bryght-room-details__actions";

    // Mute (always — toggle state shown via active class)
    const muteBtn = this.createDetailAction(
      this.t("mute", "Stumm"),
      '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>',
      () => { this.toggleMute(room.roomId).then(() => this.renderRoomDetails(room)); },
      isMuted ? "bryght-room-details__action--active" : ""
    );
    actions.appendChild(muteBtn);

    // Search in this chat: the details close and the header becomes the field.
    const searchBtn = this.createDetailAction(
      this.t("search_action", "Suchen"),
      SEARCH_ICON.replace("<svg ", '<svg width="20" height="20" '),
      () => {
        if (history.state?.panel === "details") {
          if (this.pendingRoomSearch) return;
          this.pendingRoomSearch = room;
          this.goBackInHistory();
        } else {
          this.closeRoomDetails();
          this.openRoomSearch(room);
        }
      }
    );
    actions.appendChild(searchBtn);

    // Block (DM only)
    if (isDirect) {
      const isBlocked = info.is_blocked === true;
      const blockBtn = this.createDetailAction(
        isBlocked ? this.t("unblock", "Entblocken") : this.t("block", "Blockieren"),
        '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9A7.902 7.902 0 0 1 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1A7.902 7.902 0 0 1 20 12c0 4.42-3.58 8-8 8z"/></svg>',
        () => this.handleBlock(room, info),
        isBlocked ? "bryght-room-details__action--danger" : ""
      );
      actions.appendChild(blockBtn);
    }

    // Leave (groups, non-creator)
    if (isGroup && !isCreator) {
      const leaveBtn = this.createDetailAction(
        this.t("leave_group", "Verlassen"),
        '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M10.09 15.59L11.5 17l5-5-5-5-1.41 1.41L12.67 11H3v2h9.67l-2.58 2.59zM19 3H5c-1.11 0-2 .9-2 2v4h2V5h14v14H5v-4H3v4c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/></svg>',
        () => this.handleDeleteOrLeave(room),
        "bryght-room-details__action--danger"
      );
      actions.appendChild(leaveBtn);
    }

    // Delete (DM only)
    if (isDirect) {
      const deleteBtn = this.createDetailAction(
        this.t("delete_chat", "Löschen"),
        '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>',
        () => this.handleDeleteOrLeave(room),
        "bryght-room-details__action--danger"
      );
      actions.appendChild(deleteBtn);
    }

    // Dissolve (groups, creator only)
    if (isGroup && isCreator) {
      const dissolveBtn = this.createDetailAction(
        this.t("dissolve_group", "Auflösen"),
        '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>',
        () => this.handleDissolve(room, info),
        "bryght-room-details__action--danger"
      );
      actions.appendChild(dissolveBtn);
    }

    content.appendChild(actions);

    // Media, links and docs — opens in place of the details.
    const galleryRow = document.createElement("button");
    galleryRow.type = "button";
    galleryRow.className = "bryght-room-details__row";
    galleryRow.innerHTML = `<span>${escapeHtml(this.t("gallery_title", "Medien, Links und Doks"))}</span><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>`;
    galleryRow.addEventListener("click", () => this.openMediaGallery(room));
    content.appendChild(galleryRow);

    // ── Members list (groups only) ──
    if (isGroup) {
      const membersSection = document.createElement("div");
      membersSection.className = "bryght-room-details__members";

      const membersTitle = document.createElement("h3");
      membersTitle.className = "bryght-room-details__section-title";
      const members = room.getMembers().filter(m => m.membership === "join" && m.userId.startsWith("@u_"));
      membersTitle.textContent = `${this.t("members", "Mitglieder")} (${members.length})`;
      membersSection.appendChild(membersTitle);

      // Track the currently open context menu so we can close it
      let activeContextMenu = null;
      const closeContextMenu = () => {
        if (activeContextMenu) {
          activeContextMenu.remove();
          activeContextMenu = null;
        }
      };

      members.forEach(member => {
        const memberInfo = this.getUserInfo(member.userId);
        const memberEl = document.createElement("div");
        memberEl.className = "bryght-room-details__member";

        // Roles come from Rails, never from member.powerLevel: ADR 0006 keeps
        // every human on power level 0, so the Matrix number says nothing about
        // who administers this group. A member Rails does not name — one who
        // joined after this room payload was fetched — gets no badge and no
        // menu; acting on them would fail server-side anyway.
        const memberRole = (info.members || {})[member.userId];
        const memberUserId = memberRole ? String(memberRole.user_id) : null;
        const isMemberCreator = info.creator_id && memberUserId === String(info.creator_id);
        const isSelf = member.userId === this.userId;
        const isMemberAdmin = memberRole?.role === "admin";

        const memberAvatar = document.createElement("div");
        memberAvatar.className = "bryght-room-avatar bryght-room-avatar--small";
        if (memberInfo.avatar_url) {
          memberAvatar.innerHTML = `<img src="${memberInfo.avatar_url}" alt="" />`;
        } else {
          memberAvatar.textContent = (memberInfo.name || "?").charAt(0).toUpperCase();
          memberAvatar.classList.add("bryght-room-avatar--initials");
        }
        memberEl.appendChild(memberAvatar);

        const memberName = document.createElement("span");
        memberName.className = "bryght-room-details__member-name";
        memberName.textContent = memberInfo.name;
        if (isMemberAdmin) {
          memberName.innerHTML += ` <span class="bryght-room-details__member-role">${this.t("member_role_admin", "Admin")}</span>`;
        }
        memberEl.appendChild(memberName);

        // Three-dot menu button for admins (but not for self or creator)
        if (isAdmin && !isSelf && !isMemberCreator && info.id && memberUserId) {
          memberEl.classList.add("bryght-room-details__member--clickable");
          memberEl.style.position = "relative";

          const dotsBtn = document.createElement("button");
          dotsBtn.className = "bryght-room-details__member-dots";
          dotsBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>`;
          dotsBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            closeContextMenu();

            const menu = document.createElement("div");
            menu.className = "bryght-member-context-menu";

            // Promote / Demote
            const promoteItem = document.createElement("button");
            promoteItem.className = "bryght-member-context-menu__item";
            promoteItem.textContent = isMemberAdmin
              ? this.t("demote_admin", "Admin-Rechte entziehen")
              : this.t("promote_to_admin", "Zum Admin ernennen");
            promoteItem.addEventListener("click", async (ev) => {
              ev.stopPropagation();
              closeContextMenu();
              try {
                const res = await fetch(`/chat_rooms/${info.id}/promote_member`, {
                  method: "PATCH",
                  headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": this.getCsrfToken()
                  },
                  body: JSON.stringify({ user_id: memberUserId })
                });
                if (res.ok) {
                  const data = await res.json();
                  // Write the answer back into the room payload rather than
                  // keeping a second truth beside it — the next render reads it.
                  if (info.members?.[member.userId]) {
                    info.members[member.userId].role = data.role;
                  }
                  this.renderRoomDetails(room);
                }
              } catch (err) {
                console.error("[BryghtChat] Promote failed:", err);
              }
            });
            menu.appendChild(promoteItem);

            // Remove from group
            const removeItem = document.createElement("button");
            removeItem.className = "bryght-member-context-menu__item bryght-member-context-menu__item--danger";
            removeItem.textContent = this.t("remove_from_group", "Aus Gruppe entfernen");
            removeItem.addEventListener("click", async (ev) => {
              ev.stopPropagation();
              closeContextMenu();
              const confirmMsg = this.t("confirm_remove_member", "Möchtest du dieses Mitglied wirklich aus der Gruppe entfernen?");
              if (!confirm(confirmMsg)) return;
              try {
                const res = await fetch(`/chat_rooms/${info.id}/remove_member`, {
                  method: "DELETE",
                  headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": this.getCsrfToken()
                  },
                  body: JSON.stringify({ user_id: memberUserId })
                });
                if (res.ok) {
                  if (info.members) delete info.members[member.userId];
                  this.renderRoomDetails(room);
                }
              } catch (err) {
                console.error("[BryghtChat] Remove member failed:", err);
              }
            });
            menu.appendChild(removeItem);

            memberEl.appendChild(menu);
            activeContextMenu = menu;
          });

          memberEl.appendChild(dotsBtn);
        }

        membersSection.appendChild(memberEl);
      });

      // Close context menu when clicking outside
      const handleOutsideClick = (e) => {
        if (activeContextMenu && !activeContextMenu.contains(e.target)) {
          closeContextMenu();
        }
      };
      document.addEventListener("click", handleOutsideClick);
      // Clean up when details panel is re-rendered
      const observer = new MutationObserver(() => {
        if (!membersSection.isConnected) {
          document.removeEventListener("click", handleOutsideClick);
          observer.disconnect();
        }
      });
      observer.observe(this.detailsPanel, { childList: true });

      // Edit button for admins
      if (isAdmin && info.id) {
        const editBtn = document.createElement("button");
        editBtn.className = "bryght-room-details__edit-btn button button--secondary";
        editBtn.textContent = this.t("edit_group", "Gruppe bearbeiten");
        editBtn.addEventListener("click", () => {
          const a = document.createElement("a");
          a.href = `/chat_group_rooms/${info.id}/edit`;
          a.dataset.turboStream = "true";
          document.body.appendChild(a);
          a.click();
          a.remove();
        });
        membersSection.appendChild(editBtn);
      }

      content.appendChild(membersSection);
    }

    this.detailsPanel.appendChild(content);

    // Slide in the details panel
    this.container.classList.add("is-details-active");
    this.pushPanelState("details");
    this.syncNavigationDepth();
  }

  closeRoomDetails({ animate = true } = {}) {
    this.withTransition(animate, () => this.container.classList.remove("is-details-active"));
    this.syncNavigationDepth();
  }

  // "Medien, Links und Doks" slides in over the details, the way they slide in
  // over the conversation; back slides it out and the details are still there
  // as they were. The scanner is kept per room, so reopening does not load it
  // all again.
  openMediaGallery(room) {
    if (!this.detailsPanel) return;
    this.mediaScanners ??= new Map();
    let scanner = this.mediaScanners.get(room.roomId);
    if (scanner) {
      scanner.addLoaded();
    } else {
      scanner = new RoomMediaScanner(this.client, room);
      this.mediaScanners.set(room.roomId, scanner);
    }

    this.closeMediaGallery({ animate: false });
    const onBack = () => {
      if (history.state?.panel === "media") {
        this.goBackInHistory();
      } else {
        this.closeMediaGallery();
      }
    };
    const gallery = new MediaGallery(this, room, scanner, { onBack });
    this.mediaGallery = gallery;
    this.detailsPanel.appendChild(gallery.element);
    // Commits the off-screen start before the slide begins.
    void gallery.element.offsetWidth;
    gallery.element.classList.add("is-open");
    this.pushPanelState("media");
    this.syncNavigationDepth();
  }

  handleMediaRedaction(event, room) {
    // Local echoes may still fail. Only confirmed redactions invalidate cache.
    if (event.status) return;
    const eventId = event.event?.redacts || event.getContent()?.redacts;
    const scanner = this.mediaScanners?.get(room.roomId);
    if (scanner?.redact(eventId) && this.mediaGallery?.room.roomId === room.roomId) {
      this.mediaGallery.renderTab();
    }
  }

  closeMediaGallery({ animate = true } = {}) {
    const gallery = this.mediaGallery;
    if (!gallery) return;
    this.mediaGallery = null;
    gallery.destroy();
    this.syncNavigationDepth();

    const { element } = gallery;
    if (!animate || !element.isConnected) {
      element.remove();
      return;
    }
    element.classList.remove("is-open");
    element.addEventListener("transitionend", (e) => {
      if (e.target === element) element.remove();
    });
    // No slide to wait for (reduced motion, styles not loaded): a little past
    // its 0.3s, the gallery goes anyway.
    setTimeout(() => element.remove(), 400);
  }

  createDetailAction(label, iconHtml, onClick, extraClass = "") {
    const btn = document.createElement("button");
    btn.className = `bryght-room-details__action ${extraClass}`.trim();
    btn.innerHTML = `<span class="bryght-room-details__action-icon">${iconHtml}</span><span>${escapeHtml(label)}</span>`;
    btn.addEventListener("click", onClick);
    return btn;
  }

  // ── Block handler ─────────────────────────────────────────

  async handleBlock(room, info) {
    const isBlocked = info.is_blocked === true;
    const peerUserId = info.peer_user_id;
    if (!peerUserId) return;

    if (isBlocked) {
      // Unblock
      try {
        const res = await fetch(`/user_blocks/${peerUserId}`, {
          method: "DELETE",
          headers: { "X-CSRF-Token": this.getCsrfToken() }
        });
        if (res.ok) {
          info.is_blocked = false;
          this.renderRoomDetails(room);
        }
      } catch (err) {
        console.error("[BryghtChat] Unblock failed:", err);
      }
    } else {
      // Block
      const confirmMsg = this.t("confirm_block", `Möchtest du ${info.name} wirklich blockieren? Der Chat wird gelöscht und die Person kann dich nicht mehr kontaktieren.`);
      if (!confirm(confirmMsg)) return;

      try {
        const res = await fetch("/user_blocks", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": this.getCsrfToken()
          },
          body: JSON.stringify({ blocked_user_id: peerUserId })
        });
        if (res.ok) {
          this.currentRoomId = null;
          sessionStorage.removeItem("bryght_active_room");
          this.container.classList.remove("is-room-active");
          if (this.rightPanel) {
            this.rightPanel.innerHTML = `<div class="bryght-empty-state"><p>${this.t("select_chat_title", "Wähle einen Chat")}</p></div>`;
          }
          this.syncNavigationDepth();
          this.renderRoomList();
        }
      } catch (err) {
        console.error("[BryghtChat] Block failed:", err);
      }
    }
  }

  // ── Dissolve handler ──────────────────────────────────────

  async handleDissolve(room, info) {
    const confirmMsg = this.t("confirm_dissolve", "Bist du sicher? Diese Aktion kann nicht rückgängig gemacht werden. Alle Nachrichten gehen für alle Mitglieder verloren.");
    if (!confirm(confirmMsg)) return;

    const chatRoomId = info.id;
    if (!chatRoomId) return;

    try {
      const res = await fetch(`/chat_rooms/${chatRoomId}/dissolve`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        }
      });

      if (res.ok) {
        this.currentRoomId = null;
        sessionStorage.removeItem("bryght_active_room");
        this.container.classList.remove("is-room-active");
        if (this.rightPanel) {
          this.rightPanel.innerHTML = `<div class="bryght-empty-state"><p>${this.t("select_chat_title", "Wähle einen Chat")}</p></div>`;
        }
        this.syncNavigationDepth();
        this.renderRoomList();
      } else {
        const data = await res.json().catch(() => ({}));
        console.error("[BryghtChat] Dissolve failed:", data.errors);
      }
    } catch (err) {
      console.error("[BryghtChat] Dissolve request failed:", err);
    }
  }

  // ── Date dividers ─────────────────────────────────────────

  createDateDivider(timestamp) {
    const divider = document.createElement("div");
    divider.className = "bryght-date-divider";
    divider.dataset.dayKey = getDayKey(timestamp);
    const span = document.createElement("span");
    span.textContent = formatDateDivider(timestamp);
    divider.appendChild(span);
    return divider;
  }

  ensureDateDividers(timeline) {
    if (!timeline) return;
    timeline.querySelectorAll(".bryght-date-divider").forEach((el) => el.remove());

    const messages = Array.from(timeline.querySelectorAll(".bryght-message"));
    let currentDayKey = null;

    messages.forEach((msgEl) => {
      const event = msgEl._matrixEvent;
      if (!event) return;
      const ts = event.getTs();
      const dayKey = getDayKey(ts);

      if (dayKey !== currentDayKey) {
        currentDayKey = dayKey;
        const divider = this.createDateDivider(ts);
        const prev = msgEl.previousElementSibling;
        if (prev && prev.classList.contains("bryght-unread-divider")) {
          prev.parentNode.insertBefore(divider, prev);
        } else {
          msgEl.parentNode.insertBefore(divider, msgEl);
        }
      }
    });

    // Grouping reads the final sibling order, so it has to run after the
    // dividers are in place – every caller of this method wants both.
    this.applyMessageGrouping(timeline);
  }

  // ── Message grouping ──────────────────────────────────────
  //
  // Consecutive messages from the same sender within GROUP_WINDOW_MS form a
  // burst (WhatsApp/Signal style): the sender name is rendered on the first
  // message, avatar and timestamp only on the last one. Any divider in between
  // (date or "new messages") breaks the burst.

  applyMessageGrouping(timeline) {
    if (!timeline) return;

    const GROUP_WINDOW_MS = 5 * 60 * 1000;
    let prev = null;

    Array.from(timeline.children).forEach((el) => {
      if (!el.classList.contains("bryght-message")) {
        if (prev) {
          prev.el.classList.add("bryght-message--group-end");
          prev = null;
        }
        return;
      }

      el.classList.remove("bryght-message--group-start", "bryght-message--group-end");

      const event = el._matrixEvent;
      const sender = event ? event.getSender() : null;
      const ts = event ? event.getTs() : 0;

      const continuesBurst =
        prev && sender && prev.sender === sender && Math.abs(ts - prev.ts) <= GROUP_WINDOW_MS;

      if (!continuesBurst) {
        if (prev) prev.el.classList.add("bryght-message--group-end");
        el.classList.add("bryght-message--group-start");
      }

      prev = { el, sender, ts };
    });

    if (prev) prev.el.classList.add("bryght-message--group-end");
  }

  // ── CSRF token helper ─────────────────────────────────────

  getCsrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}
